import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Slide,
  Paper,
  Button,
  Card,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, addDays, subDays } from 'date-fns';
import { getApiBaseUrl } from '../config';
import { alignToServerInterval } from '../utils/chartPeriodRange';
import {
  addServerCalendarDays,
  endOfServerDay,
  endOfServerMonth,
  endOfServerWeekSunday,
  endOfServerYear,
  formatChartTimeBucket,
  formatChartTooltipTime,
  formatInServerTz,
  getServerAdjustedNow,
  getServerZonedParts,
  pickChartTimePattern,
  serverWallTimeToUtc,
  startOfServerDay,
  startOfServerMonth,
  startOfServerWeekMonday,
  startOfServerYear,
  subscribeServerTime,
  type ServerTimePattern,
} from '../utils/serverTime';
import { getToken, getUser, isDemo } from '../services/auth';
import { getDemoActivityTracker } from '../services/demoActivityTracker';
import { getDemoRestrictionSnackbarProps } from '../constants/demoRestrictions';
import DeviceAgendaPanel from './DeviceAgendaPanel';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import HistoryIcon from '@mui/icons-material/History';
import TimerIcon from '@mui/icons-material/Timer';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Pause from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import PowerButton from './shared/PowerButton';
import DeferredNumberField from './shared/DeferredNumberField';
import { eventBus } from '../services/EventBus';
import { addFavorite, removeFavorite } from '../services/api';
import { mapaDispositivos } from '../services/consumptionService';
import { POWER_BUTTON_SIZE, ANIMATION_DURATION } from '../styles/deviceStyles';
import { 
  getDeviceSchedules, 
  updateDeviceSchedules,
  normalizeAgendaConfig,
  type TimerConfig,
  type AgendaConfig,
} from '../services/deviceSchedulesService';
import { canControlDevicesSync, canControlDevices } from '../services/permissionsService';
import { useTranslation } from 'react-i18next';
import { useOrientation } from '../hooks/useOrientation';
import { SHC_BOTTOM_NAV_CLEARANCE } from '../constants/bottomNav';
import { getListenerPool } from '../utils/heavyInitializations';

/** Tokens visuales alineados con SamsungDeviceModal / SamsungActivityChart */
const ACCENT = '#2391FF';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(255,255,255,0.45)';
const PRESS_TRANSITION = 'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease, color 160ms ease, opacity 90ms ease';

const CARD_SURFACE_SX = {
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '16px',
  background: `
    radial-gradient(90% 70% at 50% 0%, ${ACCENT}14 0%, transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.18) 100%)
  `,
} as const;

const CHART_CARD_SX = {
  ...CARD_SURFACE_SX,
  borderRadius: '20px',
  background: `
    radial-gradient(90% 70% at 50% 0%, ${ACCENT}1f 0%, transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)
  `,
} as const;

/** Glow lateral (fila de power / control), similar a Samsung powerRow. */
const controlGlowBackground = (active: boolean) => (
  active
    ? `
      radial-gradient(120% 90% at 15% 50%, ${ACCENT}28 0%, transparent 55%),
      linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)
    `
    : `
      radial-gradient(90% 70% at 50% 0%, ${ACCENT}10 0%, transparent 55%),
      linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.18) 100%)
    `
);

const SLIDER_CARD_SX = {
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '16px',
  background: `
    radial-gradient(75% 55% at 50% 85%, ${ACCENT}24 0%, transparent 55%),
    radial-gradient(90% 50% at 50% 0%, ${ACCENT}14 0%, transparent 50%),
    linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.22) 100%)
  `,
} as const;

const SECTION_TITLE_SX = {
  color: MUTED,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
};

/** Por encima del modal de teléfono (1360) y de la barra inferior (1400). El menú de MUI sale en 1300. */
const CHART_MENU_LAYER_STYLE = { zIndex: 1600 };

const MENU_PAPER_SX = {
  backgroundColor: 'rgba(18,18,20,0.98)',
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '12px',
  color: '#fff',
  '& .MuiMenuItem-root': {
    fontSize: 12,
    minHeight: 32,
    '&:hover': { backgroundColor: 'rgba(35,145,255,0.14)' },
    '&.Mui-selected': { backgroundColor: 'rgba(35,145,255,0.22)' },
  },
};

const TIMER_CHIP_TONES = {
  on: { bg: 'rgba(46, 204, 113, 0.18)', border: 'rgba(46, 204, 113, 0.55)', text: '#7dffa8' },
  off: { bg: 'rgba(255, 152, 0, 0.18)', border: 'rgba(255, 152, 0, 0.55)', text: '#ffcc80' },
  once: { bg: 'rgba(35, 145, 255, 0.18)', border: 'rgba(35, 145, 255, 0.55)', text: '#90caf9' },
  idle: { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.18)', text: 'rgba(255,255,255,0.45)' },
} as const;

function pad2(n: number): string {
  return String(Math.max(0, Number(n) || 0)).padStart(2, '0');
}

function formatTimerHms(h: number, m: number, s: number): string {
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

/** Resumen Timer en pastillas (mismo look que Agenda). */
function TimerSummaryChips({
  isPlus1,
  timerEnabled,
  isDefault,
  autoOnEnabled,
  autoOffEnabled,
  oneTimeOnly,
  autoOnHours,
  autoOnMinutes,
  autoOnSeconds,
  autoOffHours,
  autoOffMinutes,
  autoOffSeconds,
  configureHint,
  labelOn,
  labelOff,
  labelOnceLine1,
  labelOnceLine2,
}: {
  isPlus1: boolean;
  timerEnabled: boolean;
  isDefault: boolean;
  autoOnEnabled: boolean;
  autoOffEnabled: boolean;
  oneTimeOnly: boolean;
  autoOnHours: number;
  autoOnMinutes: number;
  autoOnSeconds: number;
  autoOffHours: number;
  autoOffMinutes: number;
  autoOffSeconds: number;
  configureHint: string;
  labelOn: string;
  labelOff: string;
  labelOnceLine1: string;
  labelOnceLine2: string;
}) {
  if (isDefault) {
    return (
      <Typography sx={{ color: isPlus1 ? '#666' : '#888', fontSize: '0.65rem', mt: 0.38, pl: 3.4 }}>
        {configureHint}
      </Typography>
    );
  }

  const masterDim = !timerEnabled || isPlus1;
  const chips: Array<{
    key: string;
    primary: string;
    secondary: string;
    tone: keyof typeof TIMER_CHIP_TONES;
    inactive: boolean;
    multiline?: boolean;
  }> = [
    {
      key: 'on',
      primary: autoOnEnabled ? formatTimerHms(autoOnHours, autoOnMinutes, autoOnSeconds) : '—',
      secondary: labelOn,
      tone: autoOnEnabled ? 'on' : 'idle',
      inactive: !autoOnEnabled,
    },
    {
      key: 'off',
      primary: autoOffEnabled ? formatTimerHms(autoOffHours, autoOffMinutes, autoOffSeconds) : '—',
      secondary: labelOff,
      tone: autoOffEnabled ? 'off' : 'idle',
      inactive: !autoOffEnabled,
    },
    {
      key: 'once',
      primary: labelOnceLine1,
      secondary: labelOnceLine2,
      tone: oneTimeOnly ? 'once' : 'idle',
      inactive: !oneTimeOnly,
      multiline: true,
    },
  ];

  return (
    <Box
      sx={{
        mt: 0.38,
        pl: 0.25,
        display: 'flex',
        gap: 0.55,
        overflowX: 'auto',
        overflowY: 'hidden',
        flexShrink: 0,
        alignItems: 'center',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { height: '3px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: ACCENT, borderRadius: '2px' },
        scrollbarWidth: 'thin',
        scrollbarColor: `${ACCENT} transparent`,
        opacity: masterDim ? 0.55 : 1,
      }}
    >
      {chips.map((c) => {
        const colors = TIMER_CHIP_TONES[c.tone];
        const paused = masterDim || c.inactive;
        return (
          <Box
            key={c.key}
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.15,
              px: 0.75,
              py: 0.3,
              borderRadius: '8px',
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              opacity: paused ? 0.42 : 1,
              borderStyle: paused ? 'dashed' : 'solid',
              minWidth: c.multiline ? 78 : 72,
              justifyContent: 'center',
            }}
          >
            <Typography
              component="span"
              sx={{
                color: c.multiline ? colors.text : '#fff',
                fontSize: c.multiline ? '0.58rem' : '0.72rem',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.15,
              }}
            >
              {c.primary}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: colors.text,
                fontSize: c.multiline ? '0.58rem' : '0.58rem',
                fontWeight: c.multiline ? 600 : 600,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
              }}
            >
              {c.secondary}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Porcentaje tocable (dimmer / cortinas), mismo patrón que setpoint Samsung. */
function EditablePercent({
  value,
  disabled,
  onCommit,
  ariaLabel,
}: {
  value: number;
  disabled?: boolean;
  onCommit: (n: number) => void;
  ariaLabel: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [editing]);

  useEffect(() => {
    if (disabled) setEditing(false);
  }, [disabled]);

  const begin = () => {
    if (disabled) return;
    // Arranca vacío para tipear directo; el valor actual queda como placeholder
    // y se conserva si se cancela (Escape) o se sale sin escribir.
    setDraft('');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft('');
  };

  const commit = () => {
    if (!editing) return;
    setEditing(false);
    const raw = draft.replace(/[^\d]/g, '');
    setDraft('');
    if (raw === '') return;
    const n = Math.min(100, Math.max(0, parseInt(raw, 10)));
    if (Number.isNaN(n) || n === Math.round(value)) return;
    onCommit(n);
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1, gap: 0.1 }}>
        <TextField
          inputRef={inputRef}
          value={draft}
          placeholder={String(Math.round(value))}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
            setDraft(v);
          }}
          onBlur={() => { commit(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          variant="standard"
          inputProps={{
            inputMode: 'numeric',
            'aria-label': ariaLabel,
            style: {
              textAlign: 'center',
              fontSize: '0.95rem',
              fontWeight: 750,
              letterSpacing: '-0.02em',
              color: ACCENT,
              padding: 0,
              width: 36,
            },
          }}
          sx={{
            width: 40,
            // Casi imperceptible: indica que el campo ya está vacío y se escribe directo.
            '& input::placeholder': { color: 'rgba(255,255,255,0.18)', opacity: 1 },
            '& .MuiInput-underline:before': { borderBottomColor: `${ACCENT}66` },
            '& .MuiInput-underline:after': { borderBottomColor: ACCENT },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: ACCENT,
            },
          }}
        />
        <Typography sx={{ color: ACCENT, fontSize: '0.8rem', fontWeight: 700 }}>%</Typography>
      </Box>
    );
  }

  return (
    <Typography
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      onClick={begin}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          begin();
        }
      }}
      sx={{
        color: '#fff',
        fontSize: '0.95rem',
        fontWeight: 750,
        letterSpacing: '-0.02em',
        mb: 1,
        cursor: disabled ? 'default' : 'pointer',
        WebkitTapHighlightColor: 'transparent',
        borderRadius: '8px',
        px: 0.5,
        textShadow: `0 0 22px ${ACCENT}55`,
        '&:active': disabled ? undefined : { opacity: 0.75 },
      }}
    >
      {Math.round(value)}%
    </Typography>
  );
}

// Función de mapeo para adaptar los valores del estado del cover a los que espera la lógica avanzada
const mapCoverStateToBackend = (state: string): 'open' | 'closed' | 'opening' | 'closing' | 'stopped' | 'unknown' => {
  // Si el estado es booleano, convertirlo a string
  if (typeof state === 'boolean') {
    return state ? 'open' : 'closed';
  }
  
  switch(state) {
    case 'open': return 'open';
    case 'closed': return 'closed';
    case 'opening': return 'opening';
    case 'closing': return 'closing';
    case 'stopped': return 'stopped';
    default: return 'unknown';
  }
};

// Keyframes para animación de cambio de color progresivo del marco
const spinnerKeyframes = {
  '@keyframes spinner': {
    '0%': { 
      borderColor: '#4a4a4a',
      boxShadow: 'none'
    },
    '50%': { 
      borderColor: '#2391FF',
      boxShadow: '0 0 8px rgba(35, 145, 255, 0.5)'
    },
    '100%': { 
      borderColor: '#4a4a4a',
      boxShadow: 'none'
    },
  },
};

const TIME_PICKER_FIELD_SX = {
  width: '80px',
  '& .MuiOutlinedInput-root': {
    color: 'white',
    fontSize: '0.8rem',
    '& fieldset': { borderColor: SURFACE_BORDER },
    '&:hover fieldset': { borderColor: '#2391FF' },
    '&.Mui-focused fieldset': { borderColor: '#2391FF' },
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    transform: 'scale(0.75) translate(14px, -9px)',
    '&.Mui-focused': {
      color: '#2391FF',
    },
  },
};

// TimePicker component outside of main component to prevent re-renders
const TimePickerComponent = ({ 
  hours, 
  minutes, 
  seconds, 
  onHoursChange, 
  onMinutesChange, 
  onSecondsChange,
  label: _label,
  hoursMax = 23,
}: {
  hours: number;
  minutes: number;
  seconds: number;
  onHoursChange: (value: number) => void;
  onMinutesChange: (value: number) => void;
  onSecondsChange: (value: number) => void;
  label: string;
  hoursMax?: number;
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      mb: 0.5,
      borderRadius: '12px',
      p: 1,
      border: `1px solid ${SURFACE_BORDER}`,
      background: `
        radial-gradient(90% 80% at 50% 0%, ${ACCENT}12 0%, transparent 55%),
        linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.18) 100%)
      `
    }}>
      <DeferredNumberField
        label={t('deviceModal.hours')}
        value={hours}
        min={0}
        max={hoursMax}
        emptyWhenZero
        onCommit={onHoursChange}
        size="small"
        sx={TIME_PICKER_FIELD_SX}
      />
      <DeferredNumberField
        label={t('deviceModal.minutes')}
        value={minutes}
        min={0}
        max={59}
        emptyWhenZero
        onCommit={onMinutesChange}
        size="small"
        sx={TIME_PICKER_FIELD_SX}
      />
      <DeferredNumberField
        label={t('deviceModal.seconds')}
        value={seconds}
        min={0}
        max={59}
        emptyWhenZero
        onCommit={onSecondsChange}
        size="small"
        sx={TIME_PICKER_FIELD_SX}
      />
    </Box>
  );
};

// Componente de botones de control de persiana para PLUS2PM con lógica avanzada
const CoverControlButtons: React.FC<{
  deviceId: number;
  coverAction: 'up' | 'down' | 'stop' | null;
  isConfirming: boolean;
  online?: boolean;
  authRequired?: boolean;
  onControlCover: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
  deviceState?: string; // Agregar estado del dispositivo
  coverPosition?: number; // Posición actual de la persiana
  onApertureUpdate?: (position: number) => void; // Callback para actualizar el slider
}> = React.memo(({ deviceId, coverAction, isConfirming, online = true, authRequired = false, onControlCover, deviceState, coverPosition, onApertureUpdate }) => {
  // Estado para controlar la animación
  const [isMoving, setIsMoving] = useState(false);
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  const previousCoverStateRef = useRef<string | null>(null);
  
  // Mapear el estado del dispositivo
  const mappedState = mapCoverStateToBackend(deviceState || 'stopped');
  
  // Actualizar el estado local basado en props (se ejecuta cuando cambian props)
  useEffect(() => {
    // Actualizar estado basado en las props recibidas
    if (mappedState === 'opening') {
      setIsMoving(true);
      setDirection('up');
    } else if (mappedState === 'closing') {
      setIsMoving(true);
      setDirection('down');
    } else if (mappedState === 'open' || mappedState === 'closed' || mappedState === 'stopped' || mappedState === 'unknown') {
      setIsMoving(false);
      setDirection(null);
    }
  }, [mappedState]);

  // Sincronizar el slider cuando cambia la posición de la persiana
  useEffect(() => {
    if (coverPosition !== undefined && onApertureUpdate) {
      onApertureUpdate(coverPosition);
    }
  }, [coverPosition, onApertureUpdate]);
  
  // Suscribirse a eventos de actualización de dispositivos
  useEffect(() => {
    // Función para manejar actualizaciones del dispositivo
    const handleDeviceUpdate = (data: any) => {
      if (!data || !data.id) {
        console.warn('[CoverControlButtons] Formato de datos desconocido:', data);
        return;
      }

      if (data.id === deviceId && data.tipo === 'PLUS2PM') {
        const coverState = data.estado || 'stopped';
        const mappedCoverState = mapCoverStateToBackend(coverState);
        
        // Si el dispositivo está en movimiento
        if (mappedCoverState === 'opening') {
          setIsMoving(true);
          setDirection('up');
        } 
        else if (mappedCoverState === 'closing') {
          setIsMoving(true);
          setDirection('down');
        }
        // Si el dispositivo se detuvo
        else if (mappedCoverState === 'stopped' || mappedCoverState === 'open' || mappedCoverState === 'closed') {
          setIsMoving(false);
          setDirection(null);
          
          // Detectar si el cover dejó de moverse (de opening/closing a stopped/open/closed)
          if (previousCoverStateRef.current === 'opening' || previousCoverStateRef.current === 'closing') {
            console.log('🔄 Cover dejó de moverse, consultando posición actual...');
            // Aquí podríamos hacer una consulta específica si fuera necesario
            // Por ahora, la telemetría ya debería actualizar la posición
          }
        }
        
        // Actualizar el estado anterior para la próxima comparación
        previousCoverStateRef.current = mappedCoverState;
      }
    };
    
    // Suscribirse a los eventos de actualización
    eventBus.on('device:update', handleDeviceUpdate);
    
    // Limpiar suscripciones
    return () => {
      eventBus.off('device:update', handleDeviceUpdate);
    };
  }, [deviceId]); // Solo dependemos de deviceId para evitar re-suscripciones innecesarias

  // Handler optimizado con useCallback para botón de subir
  const handleUp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isConfirming && onControlCover) {
      // Usar el estado real del dispositivo para determinar si está en movimiento
      const isCurrentlyMoving = mappedState === 'opening' || mappedState === 'closing';
      const isCurrentlyMovingUp = mappedState === 'opening';
      
      // Si el dispositivo está en movimiento hacia arriba, enviar comando STOP
      if (isCurrentlyMoving && isCurrentlyMovingUp) {
        // Enviar comando de parada
        onControlCover(deviceId, 'stop');
      } else {
        // Establecer estado local inmediatamente para UI responsiva
        setIsMoving(true);
        setDirection('up');
        onControlCover(deviceId, 'up');
      }
      
      // Simulamos la finalización de la animación después de un tiempo, por si la respuesta del servidor se demora
      setTimeout(() => {
        // Solo detenemos si sigue en la misma dirección, comprobando el estado actual 
        setIsMoving((currentIsMoving) => {
          setDirection((currentDirection) => {
            if (currentIsMoving && currentDirection === 'up') {
              return null; // Resetear dirección
            }
            return currentDirection;
          });
          
          if (currentIsMoving && direction === 'up') {
            return false; // Detener movimiento
          }
          return currentIsMoving;
        });
      }, 30000); // 30 segundos como tiempo máximo
    }
  }, [deviceId, onControlCover, isConfirming, direction, mappedState]);
  const handleDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isConfirming && onControlCover) {
      // Usar el estado real del dispositivo para determinar si está en movimiento
      const isCurrentlyMoving = mappedState === 'opening' || mappedState === 'closing';
      const isCurrentlyMovingDown = mappedState === 'closing';
      
      // Si el dispositivo está en movimiento hacia abajo, enviar comando STOP
      if (isCurrentlyMoving && isCurrentlyMovingDown) {
        // Enviar comando de parada
        onControlCover(deviceId, 'stop');
      } else {
        // Establecer estado local inmediatamente para UI responsiva
        setIsMoving(true);
        setDirection('down');
        onControlCover(deviceId, 'down');
      }
      
      // Simulamos la finalización de la animación después de un tiempo, por si la respuesta del servidor se demora
      setTimeout(() => {
        // Solo detenemos si sigue en la misma dirección, comprobando el estado actual
        setIsMoving((currentIsMoving) => {
          setDirection((currentDirection) => {
            if (currentIsMoving && currentDirection === 'down') {
              return null; // Resetear dirección
            }
            return currentDirection;
          });
          
          if (currentIsMoving && direction === 'down') {
            return false; // Detener movimiento
          }
          return currentIsMoving;
        });
      }, 30000); // 30 segundos como tiempo máximo
    }
  }, [deviceId, onControlCover, isConfirming, direction, mappedState]);

  return (
    <Box sx={{ 
      position: 'relative',
      width: POWER_BUTTON_SIZE * 2.2, // Mantener tamaño actual de DeviceModal
      height: POWER_BUTTON_SIZE + 3.5, // Mantener tamaño actual de DeviceModal
    }}>
      {/* Marco ovalado que cambia a azul durante la animación */}
      <Box
        sx={{
          position: 'absolute',
          width: 'calc(100% + 4.5px)', // Mantener estiramiento actual de DeviceModal
          height: 'calc(100% + 4.5px)', // Mantener estiramiento actual de DeviceModal
          top: '-2.25px', // Mantener posición actual de DeviceModal
          left: '-2.25px', // Mantener posición actual de DeviceModal
          borderRadius: '35px', // Mantener redondeado actual de DeviceModal
          border: isMoving 
            ? '6px solid #2391FF'
            : '6px solid #4a4a4a',
          boxShadow: isMoving ? '0 0 8px rgba(35, 145, 255, 0.5)' : 'none',
          transition: `border ${ANIMATION_DURATION}s ease-in-out, box-shadow ${ANIMATION_DURATION}s ease-in-out`,
          opacity: isConfirming ? 0.5 : 1,
          boxSizing: 'border-box',
          pointerEvents: 'none',
          ...(isConfirming && {
            animation: 'spinner 1.5s ease-in-out infinite',
            ...spinnerKeyframes,
          }),
        }}
      />
      
      {/* Botón de subir - Mantener posicionamiento actual de DeviceModal */}
      <IconButton
        onClick={handleUp}
        disabled={isConfirming || !online || authRequired}
        disableRipple
        size="small"
        sx={{
          position: 'absolute',
          left: '4.5px',
          top: '4px',
          width: POWER_BUTTON_SIZE - 4, // Mantener tamaño actual de DeviceModal
          height: POWER_BUTTON_SIZE - 4, // Mantener tamaño actual de DeviceModal
          borderRadius: '50%',
          backgroundColor: isMoving ? 'white' : 'transparent',
          border: '2px solid white',
          '&:hover': {
            backgroundColor: isMoving ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)',
          },
          p: 0,
          boxShadow: 'none',
          transition: `background-color ${ANIMATION_DURATION}s ease-in-out`,
          opacity: isConfirming ? 0.5 : 1,
        }}
      >
        {isMoving && direction === 'up' ? (
          <Pause
            sx={{
              fontSize: '1.2rem',
              color: '#2391FF',
            }}
          />
        ) : (
          <ArrowUpward
            sx={{
              fontSize: isMoving ? '1.3rem' : '1.2rem',
              color: isMoving ? '#2391FF' : 'white',
              fontWeight: isMoving ? 900 : 'normal',
            }}
          />
        )}
      </IconButton>
      
      {/* Botón de bajar - Mantener posicionamiento actual de DeviceModal */}
      <IconButton
        onClick={handleDown}
        disabled={isConfirming || !online || authRequired}
        disableRipple
        size="small"
        sx={{
          position: 'absolute',
          right: '4.5px',
          top: '4px',
          width: POWER_BUTTON_SIZE - 4, // Mantener tamaño actual de DeviceModal
          height: POWER_BUTTON_SIZE - 4, // Mantener tamaño actual de DeviceModal
          borderRadius: '50%',
          backgroundColor: isMoving ? 'white' : 'transparent',
          border: '2px solid white',
          '&:hover': {
            backgroundColor: isMoving ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)',
          },
          p: 0,
          boxShadow: 'none',
          transition: `background-color ${ANIMATION_DURATION}s ease-in-out`,
          opacity: isConfirming ? 0.5 : 1,
        }}
      >
        {isMoving && direction === 'down' ? (
          <Pause
            sx={{
              fontSize: '1.2rem',
              color: '#2391FF',
            }}
          />
        ) : (
          <ArrowDownward
            sx={{
              fontSize: isMoving ? '1.3rem' : '1.2rem',
              color: isMoving ? '#2391FF' : 'white',
              fontWeight: isMoving ? 900 : 'normal',
            }}
          />
        )}
      </IconButton>
    </Box>
  );
});

// Componente de transición personalizado - desliza desde derecha a izquierda
const Transition = React.forwardRef(function Transition(props: any, ref: any) {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface DeviceModalProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  device: {
    id: number;
    nombre: string;
    tipo: string;
    estado?: number | boolean;
    consumo?: number;
    online?: boolean;
    ip?: string;
    mac?: string;
    habitacion_id?: number | null;
    lights_brightness?: number;
    cover_0_state?: string;
    cover_position?: number;
    metadata?: any;
    orden?: number;
    tipo_artefacto?: string;
    artefacto_id?: number;
    auth_required?: boolean;
    timer_config?: TimerConfig;
    agenda_config?: AgendaConfig;
    ultimo_consumo?: number;
    ultimo_estado?: any;
    generacion?: any;
    is_favorite?: boolean;
  } | null;
  isSettingsMode?: boolean;  // Nueva prop
  // Nuevas props para timer
  deviceTimerStates?: Record<number, {
    isTimerActive: boolean;
    timerStartTime?: number;
  }>;
  // Props para control de dispositivos
  onDeviceToggle?: (device: any) => void;
  onControlCover?: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
}

// Función para formatear consumo (W o kW)
const formatConsumption = (watts: number): string => {
  if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`;
  }
  return `${watts.toFixed(2)} W`;
};

// Función para formatear fechas
// ✅ Parsear fecha manualmente para evitar problemas de timezone
// dateString viene en formato 'YYYY-MM-DD' desde el backend (ya en GMT-3)
const formatDate = (dateString: string): string => {
  // Parsear manualmente el formato YYYY-MM-DD para evitar problemas de timezone
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JavaScript son 0-indexados
    const day = parseInt(parts[2], 10);
    
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    return `${day} ${months[month]} ${year}`;
  }
  
  // Fallback: formatear en TZ del servidor
  const date = new Date(dateString);
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const zoned = getServerZonedParts(date);
  return `${zoned.day} ${months[zoned.month - 1]} ${zoned.year}`;
};

const DeviceModal: React.FC<DeviceModalProps> = ({ open, onClose, onBack, device, isSettingsMode = false, deviceTimerStates = {}, onDeviceToggle, onControlCover }) => {
  const { t, i18n } = useTranslation();
  const isHorizontal = useOrientation(); // Hook para detectar orientación
  const [isClosing, setIsClosing] = useState(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(device?.is_favorite || false);
  
  // Verificar si el usuario es demo (estado para que se actualice correctamente)
  const [userIsDemo, setUserIsDemo] = useState<boolean>(() => {
    const currentUser = getUser();
    return Boolean(currentUser && isDemo(currentUser));
  });
  
  // Actualizar userIsDemo cuando cambia el usuario o se abre el modal
  useEffect(() => {
    const currentUser = getUser();
    setUserIsDemo(Boolean(currentUser && isDemo(currentUser)));
  }, [open]);
  
  // MEJORA: Pre-inicializar pool de listeners y verificar WebSocket cuando se abre el modal
  // Esto asegura que todo esté listo antes de que el usuario presione cualquier botón
  useEffect(() => {
    if (open && device?.id) {
      // Pre-inicializar pool de listeners
      getListenerPool();
      
      // Verificar que WebSocket esté conectado
      if (typeof (window as any).WebSocketService !== 'undefined') {
        const wsService = (window as any).WebSocketService;
        if (!wsService.isConnected()) {
          // Intentar reconectar si no está conectado
          const token = localStorage.getItem('token');
          if (token) {
            wsService.connect(token);
          }
        }
        
        // Verificar que el listener de respuestas esté configurado
        wsService.setupControlResponseListener();
        
        // MEJORA: Pre-verificar disponibilidad de listeners para este dispositivo
        // Si no hay listeners disponibles, el botón mostrará error inmediatamente
        if (!wsService.hasAvailableListener()) {
          // No hacer nada aquí, el error se mostrará cuando el usuario intente usar el botón
          // Pero al menos sabemos que no hay disponibilidad
        }
      }
      
      // MEJORA: Pre-verificar que mapaDispositivos esté cargado
      // Si no está cargado, esperar a que se cargue para evitar delays en el primer clic
      if (typeof (window as any).isMapaDispositivosCargado === 'function') {
        if (!(window as any).isMapaDispositivosCargado()) {
          // Escuchar evento de estructura actualizada para saber cuando está listo
          const checkMapaLoaded = () => {
            if ((window as any).isMapaDispositivosCargado()) {
              eventBus.off('estructura:actualizada', checkMapaLoaded);
            }
          };
          eventBus.on('estructura:actualizada', checkMapaLoaded);
          
          // Limpiar listener cuando el modal se cierra
          return () => {
            eventBus.off('estructura:actualizada', checkMapaLoaded);
          };
        }
      }
    }
  }, [open, device?.id]); // Cuando se abre el modal o cambia el dispositivo

  // Estilos específicos para PC - MÁS AGRESIVOS
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'date-picker-pc-styles';
    style.textContent = `
      /* FORZAR ESTILOS EN PC */
      @media (min-width: 769px) {
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1) !important;
          background-color: #ccc !important;
          cursor: pointer !important;
          padding: 4px !important;
          border-radius: 4px !important;
          width: 16px !important;
          height: 16px !important;
          opacity: 1 !important;
          display: block !important;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          background-color: #aaa !important;
        }
        
        /* Forzar fondo gris en PC */
        .MuiTextField-root input[type="date"] {
          background-color: #666 !important;
        }
        
        .MuiTextField-root .MuiOutlinedInput-root {
          background-color: #666 !important;
        }
      }
    `;
    
    // Remover estilos anteriores si existen
    const existingStyle = document.getElementById('date-picker-pc-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
    
    return () => {
      const styleToRemove = document.getElementById('date-picker-pc-styles');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);
  
  const [timerDialogOpen, setTimerDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  
  // Estados para el gráfico de consumo (igual que HistoricalReports)
  type PeriodOption = '1hour' | '6hours' | '12hours' | '24hours' | '7days' | '30days' | '12months' | 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'billing' | 'custom';
  type IntervalOption = 'minute' | '5minutes' | '15minutes' | '30minutes' | 'hour' | 'day';
  
  const [period, setPeriod] = useState<PeriodOption>('today'); // ✅ REFACTORIZACIÓN: Por defecto: hoy (igual que HistoricalReports)
  const [interval, setChartInterval] = useState<IntervalOption>('hour'); // Default de 'today' (evita un fetch extra al abrir)
  // ✅ REFACTORIZACIÓN: Eliminar displayMode - siempre mostramos potencia en gráfico y energía en leyendas
  const [deviceChartData, setDeviceChartData] = useState<any[]>([]);
  const [deviceChartDataEnergy, setDeviceChartDataEnergy] = useState<any[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const chartLoadIdRef = useRef(0);
  const historyLoadIdRef = useRef(0);
  
  // Estados para el historial de actividades
  const [activityHistory, setActivityHistory] = useState<any[]>([]);
  const [loadingActivityHistory, setLoadingActivityHistory] = useState(false);
  
  // Función para calcular rango de fechas (igual que HistoricalReports)
  const getDateRange = useCallback((): { start: string; end: string; startDate: Date; endDate: Date } => {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (period) {
      case '1hour':
        start = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6hours':
        start = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '12hours':
        start = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        break;
      case '24hours':
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7days':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '12months':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case 'today':
        start = startOfDay(now);
        end = now; // ✅ REFACTORIZACIÓN: Solo hasta la hora actual, no hasta el final del día
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
        break;
      case 'thisWeek':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'thisYear':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          const startD = new Date(customStartDate);
          const endD = new Date(customEndDate);
          return {
            start: startD.toISOString(),
            end: endD.toISOString(),
            startDate: startD,
            endDate: endD
          };
        }
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      startDate: start,
      endDate: end
    };
  }, [period, customStartDate, customEndDate]);

  // ✅ FASE 3: Función para obtener timeout dinámico optimizado para Continuous Aggregates
  // Las consultas ahora son 80-90% más rápidas, permitiendo timeouts más cortos
  const getDynamicTimeout = useCallback((): number => {
    const { startDate, endDate } = getDateRange();
    const periodMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const periodDays = periodMinutes / (24 * 60);

    // ✅ Timeouts optimizados para Continuous Aggregates
    if (periodDays <= 1) return 120000;      // 2 minutos para períodos <= 1 día (antes: 5 minutos)
    if (periodDays <= 7) return 120000;      // 2 minutos para períodos <= 7 días (antes: 5 minutos)
    if (periodDays <= 30) return 180000;     // 3 minutos para períodos <= 30 días (antes: 5 minutos)
    if (periodDays <= 90) return 300000;      // 5 minutos para períodos <= 90 días (antes: 10 minutos)
    return 600000;                             // 10 minutos para períodos > 90 días (antes: 15 minutos)
  }, [getDateRange]);

  // Cargar potencia (gráfico) y energía (leyenda) en paralelo; el spinner sale con la potencia
  const loadDeviceChartData = useCallback(async () => {
    if (!device?.id) return;
    const loadId = ++chartLoadIdRef.current;
    setLoadingChart(true);
    setDeviceChartDataEnergy([]);

    const token = getToken();
    const { start, end } = getDateRange();
    const params: any = {
      start_date: start,
      end_date: end,
      interval,
      device_ids: String(device.id)
    };
    const dynamicTimeout = getDynamicTimeout();
    const headers = { Authorization: `Bearer ${token}` };
    const powerPromise = axios.get(`${getApiBaseUrl()}/historical/consumption/by-device-time-series`, {
      headers,
      params,
      timeout: dynamicTimeout
    });
    const energyPromise = axios.get(`${getApiBaseUrl()}/historical/consumption/energy-by-device-time-series`, {
      headers,
      params,
      timeout: dynamicTimeout
    });

    try {
      const powerResponse = await powerPromise;
      if (loadId !== chartLoadIdRef.current) return;
      if (powerResponse.data.success) {
        setDeviceChartData(powerResponse.data.data || []);
      } else {
        setDeviceChartData([]);
      }
    } catch {
      if (loadId !== chartLoadIdRef.current) return;
      setDeviceChartData([]);
    } finally {
      if (loadId === chartLoadIdRef.current) {
        setLoadingChart(false);
      }
    }

    try {
      const energyResponse = await energyPromise;
      if (loadId !== chartLoadIdRef.current) return;
      if (energyResponse.data.success) {
        setDeviceChartDataEnergy(energyResponse.data.data || []);
      } else {
        setDeviceChartDataEnergy([]);
      }
    } catch {
      if (loadId !== chartLoadIdRef.current) return;
      setDeviceChartDataEnergy([]);
    }
  }, [device?.id, interval, getDynamicTimeout, getDateRange]);

  useEffect(() => {
    if (!device?.id) return;
    loadDeviceChartData();
    return () => {
      chartLoadIdRef.current += 1;
    };
  }, [device?.id, period, interval, customStartDate, customEndDate, loadDeviceChartData]);

  useEffect(() => subscribeServerTime((ev) => {
    if (!ev.offsetCorrected || !device?.id) return;
    loadDeviceChartData();
  }), [device?.id, loadDeviceChartData]);

  const loadActivityHistory = useCallback(async () => {
    if (!device?.id) return;
    const loadId = ++historyLoadIdRef.current;
    setLoadingActivityHistory(true);
    try {
      const token = getToken();
      const now = new Date();
      const startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const response = await axios.get(`${getApiBaseUrl()}/historical/states`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate.toISOString(),
          end_date: now.toISOString(),
          device_id: device.id,
          limit: 5
        },
        timeout: 30000
      });

      if (loadId !== historyLoadIdRef.current) return;
      if (response.data.success) {
        const states = (response.data.data || [])
          .filter((state: any) => state.dispositivo_id === device.id)
          .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 5);
        setActivityHistory(states);
      }
    } catch {
      if (loadId !== historyLoadIdRef.current) return;
      setActivityHistory([]);
    } finally {
      if (loadId === historyLoadIdRef.current) {
        setLoadingActivityHistory(false);
      }
    }
  }, [device?.id]);

  useEffect(() => {
    if (!device?.id || !open) return;
    loadActivityHistory();
    return () => {
      historyLoadIdRef.current += 1;
    };
  }, [device?.id, open, loadActivityHistory]);

  // ✅ REFACTORIZACIÓN: Función para formatear datos del gráfico - siempre usar potencia para el gráfico
  const formatDeviceChartData = useMemo(() => {
    // Siempre usar datos de potencia para el gráfico
    const sourceData = deviceChartData;
    if (!sourceData.length || !device?.id) return [];

    // Calcular rango de fechas (hora local del servidor)
    const now = getServerAdjustedNow();
    let startDate: Date;
    let endDate: Date = now;

    switch (period) {
      case '1hour':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6hours':
        startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '12hours':
        startDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        break;
      case '24hours':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '12months':
        startDate = addServerCalendarDays(now, -365);
        break;
      case 'today':
        startDate = startOfServerDay(now);
        endDate = endOfServerDay(now);
        break;
      case 'yesterday': {
        const yesterday = addServerCalendarDays(now, -1);
        startDate = startOfServerDay(yesterday);
        endDate = endOfServerDay(yesterday);
        break;
      }
      case 'thisWeek':
        startDate = startOfServerWeekMonday(now);
        endDate = endOfServerWeekSunday(now);
        break;
      case 'thisMonth':
        startDate = startOfServerMonth(now);
        endDate = endOfServerMonth(now);
        break;
      case 'thisYear':
        startDate = startOfServerYear(now);
        endDate = endOfServerYear(now);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        }
        break;
      default:
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }
    const periodHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    const timeFormat: ServerTimePattern = pickChartTimePattern(periodHours, interval);

    // Generar todos los puntos de tiempo (labels en TZ servidor + ts para tooltip)
    const timePoints: Array<{ time: string; ts: number }> = [];
    let current = alignToServerInterval(new Date(startDate), interval);
    const end = new Date(endDate);
    
    let intervalMs: number;
    switch (interval) {
      case 'minute': intervalMs = 60 * 1000; break;
      case '5minutes': intervalMs = 5 * 60 * 1000; break;
      case '15minutes': intervalMs = 15 * 60 * 1000; break;
      case '30minutes': intervalMs = 30 * 60 * 1000; break;
      case 'hour': intervalMs = 60 * 60 * 1000; break;
      case 'day': intervalMs = 24 * 60 * 60 * 1000; break;
      default: intervalMs = 60 * 60 * 1000;
    }
    
    while (current <= end) {
      timePoints.push({
        time: formatInServerTz(current, timeFormat),
        ts: current.getTime(),
      });
      current = new Date(current.getTime() + intervalMs);
    }

    const timeFormatCache = new Map<string, string>();
    const getFormattedTime = (timeBucket: string | Date): string => {
      const timeStr = typeof timeBucket === 'string' ? timeBucket : timeBucket.toISOString();
      if (timeFormatCache.has(timeStr)) {
        return timeFormatCache.get(timeStr)!;
      }
      const formatted = formatChartTimeBucket(timeBucket, timeFormat);
      timeFormatCache.set(timeStr, formatted);
      return formatted;
    };

    // Mapear datos del dispositivo
    const timeMap = new Map<string, number>();
    sourceData
      .filter(item => item.dispositivo_id === device.id)
      .forEach(item => {
        if (!item.time_bucket) return;
        const timeKey = getFormattedTime(item.time_bucket);
        timeMap.set(timeKey, item.consumo_promedio || 0);
      });

    // Mostrar 0 cuando no hay datos del backend, solo hasta la hora actual del servidor
    const currentTime = getServerAdjustedNow();
    const currentParts = getServerZonedParts(currentTime);
    return timePoints.map(({ time, ts }) => {
      const consumoValue = timeMap.get(time);
      const dataPoint: any = { time, ts };
      
      let isFuture = false;
      try {
        if (timeFormat === 'HH:mm') {
          const [hours, minutes] = time.split(':').map(Number);
          const timeDate = serverWallTimeToUtc(
            currentParts.year, currentParts.month, currentParts.day, hours, minutes || 0,
          );
          isFuture = timeDate > currentTime;
        } else if (timeFormat === 'dd/MM HH:mm') {
          const parts = time.split(' ');
          if (parts.length === 2) {
            const [dayMonth, hourMinute] = parts;
            const [day, month] = dayMonth.split('/').map(Number);
            const [hours, minutes] = hourMinute.split(':').map(Number);
            const timeDate = serverWallTimeToUtc(
              currentParts.year, month, day, hours, minutes || 0,
            );
            if (day === currentParts.day && month === currentParts.month) {
              isFuture = timeDate > currentTime;
            }
          }
        }
      } catch (e) {
        // Si hay error parseando, no considerar futuro
      }
      
      if (consumoValue !== undefined) {
        // Hay datos del backend para este punto (puede ser 0 o > 0)
        dataPoint.consumo = Math.round(consumoValue);
      } else if (!isFuture) {
        // No hay datos del backend, pero estamos antes de la hora actual
        // Asumir que el dispositivo está apagado (0W)
        dataPoint.consumo = 0;
      }
      // Si es futuro y no hay datos, dejar undefined (no inventar valores)
      
      return dataPoint;
    });
  }, [deviceChartData, device?.id, interval, period, customStartDate, customEndDate]);

  // ✅ REFACTORIZACIÓN: Calcular energía total del dispositivo desde deviceChartDataEnergy (igual que HistoricalReports)
  const deviceEnergyTotal = useMemo(() => {
    if (!deviceChartDataEnergy.length || !device?.id) return 0;
    
    // Sumar todos los wh del dispositivo (misma lógica que calculateDeviceEnergyMap en HistoricalReports)
    let total = 0;
    deviceChartDataEnergy
      .filter(item => item.dispositivo_id === device.id)
      .forEach((item: any) => {
        const whValue = item.wh !== null && item.wh !== undefined ? Math.abs(item.wh) : 0;
        total += whValue;
      });
    return total;
  }, [deviceChartDataEnergy, device?.id]);

  // ✅ REFACTORIZACIÓN: Función para obtener intervalos disponibles según período (igual que HistoricalReports)
  // Función para obtener TODOS los intervalos disponibles
  // El backend maneja automáticamente qué vista materializada usar según período e intervalo
  // Si el intervalo no coincide exactamente con la vista, hace agregación adicional eficiente
  const getAvailableIntervals = useCallback((): IntervalOption[] => {
    // Retornar TODAS las opciones disponibles siempre
    // El backend optimiza automáticamente según período e intervalo solicitado
    return ['minute', '5minutes', '15minutes', '30minutes', 'hour', 'day'];
  }, []);

  // Función para obtener intervalo por defecto sugerido según período
  // Sugiere un intervalo razonable, pero el usuario puede elegir cualquier intervalo disponible
  const getDefaultInterval = useCallback((currentPeriod: PeriodOption): IntervalOption => {
    // Para períodos "custom" o "billing", calcular duración y sugerir intervalo razonable
    if (currentPeriod === 'custom' || currentPeriod === 'billing') {
      const { startDate, endDate } = getDateRange();
      const periodDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // Sugerir intervalo según duración del período
      if (periodDays <= 1) {
        return '15minutes'; // Períodos cortos: 15 minutos
      } else if (periodDays <= 7) {
        return 'hour'; // Períodos medios: hora
      } else if (periodDays <= 30) {
        return 'hour'; // Períodos de un mes: hora
      } else {
        return 'day'; // Períodos largos: día
      }
    }
    
    // Para períodos fijos, sugerir intervalo razonable
    const defaultIntervals: Record<string, IntervalOption> = {
      '1hour': '5minutes',
      '6hours': '15minutes',
      '12hours': '30minutes',
      '24hours': 'hour',
      '7days': 'hour',
      '30days': 'hour',
      '12months': 'day',
      'today': 'hour',
      'yesterday': 'hour',
      'thisWeek': 'hour',
      'thisMonth': 'hour',
      'thisYear': 'day',
    };
    
    return defaultIntervals[currentPeriod] || 'hour';
  }, [getDateRange]);

  // ✅ REFACTORIZACIÓN: Cuando cambia el período, seleccionar automáticamente el segundo intervalo permitido (igual que HistoricalReports)
  useEffect(() => {
    const defaultInterval = getDefaultInterval(period);
    setChartInterval(defaultInterval);
  }, [period, getDefaultInterval]); // Ejecutar cuando cambia el período para establecer el segundo intervalo permitido

  // CustomTooltip para el gráfico - Compacto, una sola línea, posicionado debajo
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Tomar solo la primera entrada válida para mostrar una sola línea
      const entry = payload.find((e: any) => e && e.value !== null && e.value !== undefined) || payload[0];
      if (!entry) return null;
      
      const value = entry.value || 0;
      const absValue = Math.abs(value);
      // ✅ REFACTORIZACIÓN: Siempre mostrar potencia en tooltip
      const formattedValue = absValue >= 1000 
        ? `${(value / 1000).toFixed(2)} kW` 
        : `${Math.round(value)} W`;
      const tipLabel = entry.payload?.ts != null
        ? formatChartTooltipTime(entry.payload.ts, i18n.language)
        : label;
      
      return (
        <Box sx={{ 
          backgroundColor: 'rgba(18,18,20,0.96)', 
          border: `1px solid ${SURFACE_BORDER}`, 
          borderRadius: '12px', 
          padding: '8px 12px', 
          boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          minWidth: 150
        }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginBottom: 0.5, fontSize: 10 }}>{tipLabel}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
              <Box sx={{ width: 8, height: 8, backgroundColor: entry.color || ACCENT, borderRadius: '50%', flexShrink: 0 }} />
              <Typography sx={{ color: '#fff', fontSize: 11 }}>
                {device?.nombre || 'Consumo'}
              </Typography>
            </Box>
            <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700, marginLeft: 1.5 }}>
              {formattedValue}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Handler para cambio de período
  const handlePeriodChange = (newPeriod: PeriodOption) => {
    // Tracking para usuarios Demo
    const currentUser = getUser();
    if (currentUser && isDemo(currentUser)) {
      const tracker = getDemoActivityTracker();
      tracker.trackFilterChange('period', newPeriod, {
        screen: 'device_modal',
        deviceId: device?.id || null
      });
    }
    
    setPeriod(newPeriod);
    setChartInterval(getDefaultInterval(newPeriod));
    if (newPeriod === 'custom') {
      setCustomDateDialogOpen(true);
    }
  };

  // Handler para confirmar fecha personalizada
  const handleCustomDateConfirm = () => {
    // Tracking para usuarios Demo
    const currentUser = getUser();
    if (currentUser && isDemo(currentUser)) {
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'confirm_custom_date_button',
        device?.id || null,
        `Confirmar fecha personalizada: ${customStartDate} - ${customEndDate}`,
        {
          screen: 'device_modal',
          deviceId: device?.id || null
        }
      );
    }
    
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start >= end) {
        // Mostrar error si es necesario
        return;
      }
      const periodDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      setChartInterval(periodDays <= 1 ? '15minutes' : periodDays <= 30 ? 'hour' : 'day');
      setCustomDateDialogOpen(false);
    }
  };

  const formatDayRangeForCustomInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleDeviceModalNavigateDay = (direction: 'previous' | 'next') => {
    const today = startOfDay(new Date());
    let targetDate: Date;
    if (direction === 'previous') {
      if (period === 'custom' && customStartDate) {
        targetDate = subDays(new Date(customStartDate), 1);
      } else {
        targetDate = subDays(today, 1);
      }
    } else {
      if (period === 'custom' && customStartDate) {
        targetDate = addDays(new Date(customStartDate), 1);
      } else {
        return;
      }
    }
    const startDate = startOfDay(targetDate);
    const endDate = startOfDay(addDays(targetDate, 1));
    setCustomStartDate(formatDayRangeForCustomInput(startDate));
    setCustomEndDate(formatDayRangeForCustomInput(endDate));
    setPeriod('custom');
    setChartInterval('15minutes');
  };

  const handleDeviceModalGoToToday = () => {
    setPeriod('today');
    setChartInterval('hour');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const deviceModalCanNavigateNextDay =
    period === 'custom' &&
    !!customStartDate &&
    new Date(customStartDate).getTime() < startOfDay(new Date()).getTime();

  const [autoOnEnabled, setAutoOnEnabled] = useState<boolean>(false);
  const [autoOffEnabled, setAutoOffEnabled] = useState<boolean>(false);
  const [oneTimeOnly, setOneTimeOnly] = useState<boolean>(false);
  
  // Timer picker states
  const [autoOnHours, setAutoOnHours] = useState<number>(0);
  const [autoOnMinutes, setAutoOnMinutes] = useState<number>(0);
  const [autoOnSeconds, setAutoOnSeconds] = useState<number>(0);
  const [autoOffHours, setAutoOffHours] = useState<number>(0);
  const [autoOffMinutes, setAutoOffMinutes] = useState<number>(0);
  const [autoOffSeconds, setAutoOffSeconds] = useState<number>(0);
  
  // Estados para el dialog de Agenda
  const [agendaDialogOpen, setAgendaDialogOpen] = useState<boolean>(false);
  const [agendaStep, setAgendaStep] = useState<number>(1);
  const [selectedDays, setSelectedDays] = useState<number[]>([]); // Sin días seleccionados por defecto
  const [specificDaysEnabled, setSpecificDaysEnabled] = useState<boolean>(false);
  const [specificMonthsEnabled, setSpecificMonthsEnabled] = useState<boolean>(false);
  const [selectedDaysOfMonth, setSelectedDaysOfMonth] = useState<number[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  
  // Estados para múltiples horarios
  const [scheduleTimes, setScheduleTimes] = useState<Array<{hours: number, minutes: number, seconds: number}>>([{hours: 0, minutes: 0, seconds: 0}]);
  const [actionType, setActionType] = useState<string>('');
  const [toggleAfterSeconds, setToggleAfterSeconds] = useState<number>(0);
  const [toggleEnabled, setToggleEnabled] = useState<boolean>(false);
  const [timeError, setTimeError] = useState<string>('');
  const [durationError, setDurationError] = useState<string>('');
  
  // Estados para switches de habilitado/deshabilitado
  const [timerEnabled, setTimerEnabled] = useState<boolean>(false);
  const [agendaEnabled, setAgendaEnabled] = useState<boolean>(false);
  
  // Estados para controlar si se debe abrir el diálogo
  const [shouldOpenTimerDialog, setShouldOpenTimerDialog] = useState<boolean>(false);
  const [shouldOpenAgendaDialog, setShouldOpenAgendaDialog] = useState<boolean>(false);
  
  // Estado para verificar permisos de control
  const [canControl, setCanControl] = useState<boolean>(true);
  const [permissionSnackbar, setPermissionSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: ''
  });
  
  // Verificar permisos de control al montar o cuando cambia el dispositivo
  useEffect(() => {
    const checkPermissions = async () => {
      const user = getUser();
      if (user && isDemo(user)) {
        setCanControl(false);
        return;
      }
      
      if (!device || !device.habitacion_id) {
        setCanControl(true); // Si no hay habitación, permitir (por compatibilidad)
        return;
      }
      
      // Verificar síncronamente primero (desde cache)
      const hasPermissionSync = canControlDevicesSync(device.habitacion_id);
      if (hasPermissionSync) {
        setCanControl(true);
        return;
      }
      
      // Si no hay cache, verificar asíncronamente
      const hasPermission = await canControlDevices(device.habitacion_id);
      setCanControl(hasPermission);
    };
    
    checkPermissions();
  }, [device]);

  // Estados para barra de brillo DIMMER2
  const [currentBrightness, setCurrentBrightness] = useState<number>(device?.lights_brightness || 50); // Valor por defecto
  const [localBrightness, setLocalBrightness] = useState<number>(device?.lights_brightness || 50); // Estado local para el slider
  const [isUserInteracting, setIsUserInteracting] = useState<boolean>(false); // Flag para evitar interferencia de telemetría y recargas durante interacción
  // MEJORA: Ref para verificar isUserInteracting de forma síncrona en los useEffects
  const isUserInteractingRef = useRef<boolean>(false);
  // Ref para almacenar si hay un cambio de estado pendiente que requiere recarga del historial
  const pendingHistoryReloadRef = useRef<boolean>(false);
  
  // Estados para control de apertura PLUS2PM
  const [currentAperture, setCurrentAperture] = useState<number>(device?.cover_position ?? 50); // Valor por defecto
  const [localAperture, setLocalAperture] = useState<number>(device?.cover_position ?? 50); // Estado local para el slider
  const [initialAperture, setInitialAperture] = useState<number>(device?.cover_position ?? 50); // Valor inicial para detectar dirección
  
  // Estados para control de persiana PLUS2PM (solo para PLUS2PM)
  const [coverActions, setCoverActions] = useState<Record<number, 'up' | 'down' | 'stop' | null>>({});
  const [confirmingDevices, setConfirmingDevices] = useState<Set<number>>(new Set());
  const [, setPendingCoverActions] = useState<Record<number, 'up' | 'down' | 'stop'>>({});
  
  // Estado local para el dispositivo (para actualizaciones en tiempo real)
  const [localDevice, setLocalDevice] = useState(device);
  // Ref para almacenar el estado anterior y detectar cambios
  const previousEstadoRef = useRef<any>(device?.estado);
  
  // Verificar si el dispositivo es PLUS1 o PLUS2PM (no soportan Timer) - usar useMemo para recalcular cuando cambie localDevice
  const isPlus1 = useMemo(() => {
    const tipo = (localDevice || device)?.tipo;
    return tipo === 'PLUS1' || tipo === 'PLUS2PM';
  }, [localDevice, device]);
  
  // Sincronizar dispositivo local con el prop
  useEffect(() => {
    if (device) {
      setLocalDevice(device);
      previousEstadoRef.current = device.estado;
    }
  }, [device]);
  
  // Efecto CRÍTICO: Resetear TODO cuando cambia el dispositivo
  useEffect(() => {
    if (device) {
      console.log(`🔄 CAMBIO DE DISPOSITIVO: ${device.id} - ${device.nombre}`);
      
      // RESETEAR TODO INMEDIATAMENTE
      setTimerEnabled(false);
      setAutoOnEnabled(false);
      setAutoOffEnabled(false);
      setOneTimeOnly(false);
      setAutoOnHours(0);
      setAutoOnMinutes(0);
      setAutoOnSeconds(0);
      setAutoOffHours(0);
      setAutoOffMinutes(0);
      setAutoOffSeconds(0);
      setAgendaEnabled(false);
      setSelectedDays([]);
      setSpecificDaysEnabled(false);
      setSpecificMonthsEnabled(false);
      setSelectedDaysOfMonth([]);
      setSelectedMonths([]);
      setScheduleTimes([{ hours: 0, minutes: 0, seconds: 0 }]);
      setActionType('');
      setToggleAfterSeconds(0);
      setToggleEnabled(false);
      setTimeError('');
      setDurationError('');
      
      // Cerrar todos los diálogos
      setTimerDialogOpen(false);
      setAgendaDialogOpen(false);
      setScheduleDialogOpen(false);
      
      console.log(`✅ ESTADO RESETEADO para ${device.nombre}`);
    }
  }, [device]);

  // Funciones para cargar y guardar configuraciones del dispositivo
  const saveDeviceSchedules = useCallback(async () => {
    if (!device) return;
    
    try {
      console.log(`💾 GUARDANDO SOLO configuración de Timer para ${device.nombre}:`, {
        timerEnabled
      });
      
      // Preparar configuración del Timer (CON enabled actual - no lo cambia)
      const timerConfig: TimerConfig = {
        enabled: timerEnabled, // Mantener el valor actual, no cambiarlo
        autoOnEnabled,
        autoOffEnabled,
        oneTimeOnly,
        autoOnHours,
        autoOnMinutes,
        autoOnSeconds,
        autoOffHours,
        autoOffMinutes,
        autoOffSeconds
      };
      
      console.log('📤 Enviando configuración de Timer (con enabled actual):', { timerConfig });
      
      // Guardar configuración del Timer (mantiene enabled actual)
      const result = await updateDeviceSchedules(device.id, timerConfig, undefined);
      
      if (result.success) {
        console.log('✅ Configuración de Timer guardada y aplicada al dispositivo');
        
        // Mostrar feedback al usuario
        if (timerEnabled) {
          console.log('🔄 Timer aplicado al dispositivo Shelly');
          // Aquí podrías agregar una notificación visual al usuario
        } else {
          console.log('🔄 Timer removido del dispositivo Shelly');
          // Aquí podrías agregar una notificación visual al usuario
        }
      } else {
        console.error('❌ Error guardando configuración de Timer:', result.message);
      }
    } catch (error) {
      console.error('❌ Error guardando configuración de Timer del dispositivo:', error);
    }
  }, [device, timerEnabled, autoOnEnabled, autoOffEnabled, oneTimeOnly, autoOnHours, autoOnMinutes, autoOnSeconds, autoOffHours, autoOffMinutes, autoOffSeconds]);

  // Función para guardar SOLO configuración de Agenda
  const saveAgendaSchedules = useCallback(async () => {
    if (!device) return;
    
    try {
      console.log(`💾 GUARDANDO SOLO configuración de Agenda para ${device.nombre}:`, {
        agendaEnabled
      });
      
      // Preparar configuración de la Agenda (CON enabled actual - no lo cambia)
      const agendaConfig: AgendaConfig = normalizeAgendaConfig({
        enabled: agendaEnabled, // Mantener el valor actual, no cambiarlo
        selectedDays,
        specificDaysEnabled,
        specificMonthsEnabled,
        selectedDaysOfMonth,
        selectedMonths,
        scheduleTimes,
        scheduleSeconds: scheduleTimes.map(() => 0), // Mapear scheduleTimes a scheduleSeconds
        actionType,
        toggleAfterSeconds,
        toggleEnabled
      });
      
      console.log('📤 Enviando configuración de Agenda (con enabled actual):', { agendaConfig });
      
      // Guardar configuración de la Agenda (mantiene enabled actual)
      const result = await updateDeviceSchedules(device.id, undefined, agendaConfig);
      
      if (result.success) {
        console.log('✅ Configuración de Agenda guardada y aplicada al dispositivo');
        
        // Mostrar feedback al usuario
        if (agendaEnabled) {
          console.log('🔄 Agenda aplicada al dispositivo Shelly');
          // Aquí podrías agregar una notificación visual al usuario
        } else {
          console.log('🔄 Agenda removida del dispositivo Shelly');
          // Aquí podrías agregar una notificación visual al usuario
        }
      } else {
        console.error('❌ Error guardando configuración de Agenda:', result.message);
      }
    } catch (error) {
      console.error('❌ Error guardando configuración de Agenda del dispositivo:', error);
    }
  }, [device, agendaEnabled, selectedDays, specificDaysEnabled, specificMonthsEnabled, selectedDaysOfMonth, selectedMonths, scheduleTimes, actionType, toggleAfterSeconds, toggleEnabled]);

  // Función para guardar con valores específicos (para switches de Timer)
  const saveDeviceSchedulesWithValues = useCallback(async (timerEnabledValue: boolean, _agendaEnabledValue: boolean) => {
    if (!device) return;
    
    try {
      // Solo Timer: la agenda la maneja DeviceAgendaPanel (no pisar jobs[])
      const timerConfig: TimerConfig = {
        enabled: timerEnabledValue,
        autoOnEnabled,
        autoOffEnabled,
        oneTimeOnly,
        autoOnHours,
        autoOnMinutes,
        autoOnSeconds,
        autoOffHours,
        autoOffMinutes,
        autoOffSeconds
      };
      
      const result = await updateDeviceSchedules(device.id, timerConfig, undefined);
      
      if (result.success) {
        console.log('✅ Configuraciones guardadas correctamente con valores específicos');
      } else {
        console.error('❌ Error guardando configuraciones:', result.message);
      }
    } catch (error) {
      console.error('❌ Error guardando configuraciones del dispositivo:', error);
    }
  }, [device, autoOnEnabled, autoOffEnabled, oneTimeOnly, autoOnHours, autoOnMinutes, autoOnSeconds, autoOffHours, autoOffMinutes, autoOffSeconds]);

  // Función para cargar configuraciones del dispositivo
  const loadDeviceSchedules = useCallback(async () => {
    if (!device) return;
    
    try {
      let timerConfig;
      let agendaConfig;
      
      // Timer: usar el mapa de inmediato (no bloquear UI esperando probe Shelly de agenda).
      // Agenda: DeviceAgendaPanel hidrata desde device.agenda_config y refresca en background.
      if (device.timer_config) {
        timerConfig = device.timer_config;
        agendaConfig = device.agenda_config;
      } else {
        const schedules = await getDeviceSchedules(device.id, false);
        timerConfig = schedules.timer_config;
        agendaConfig = schedules.agenda_config;
      }
      
      console.log(`📋 CONFIGURACIONES para ${device.nombre}:`, {
        timer_enabled: timerConfig?.enabled,
        agenda_enabled: agendaConfig?.enabled,
        schedule_times_count: agendaConfig?.scheduleTimes?.length
      });
      
      // APLICAR Timer config
      setTimerEnabled(timerConfig.enabled);
      setAutoOnEnabled(timerConfig.autoOnEnabled);
      setAutoOffEnabled(timerConfig.autoOffEnabled);
      setOneTimeOnly(timerConfig.oneTimeOnly);
      setAutoOnHours(timerConfig.autoOnHours);
      setAutoOnMinutes(timerConfig.autoOnMinutes);
      setAutoOnSeconds(timerConfig.autoOnSeconds);
      setAutoOffHours(timerConfig.autoOffHours);
      setAutoOffMinutes(timerConfig.autoOffMinutes);
      setAutoOffSeconds(timerConfig.autoOffSeconds);
      
      // APLICAR Agenda config (legacy state; UI real en DeviceAgendaPanel)
      const ag = normalizeAgendaConfig(agendaConfig);
      setAgendaEnabled(ag.enabled);
      const first = ag.jobs[0];
      setSelectedDays(first?.selectedDays || []);
      setSpecificDaysEnabled(Boolean(first?.specificDaysEnabled));
      setSpecificMonthsEnabled(Boolean(first?.specificMonthsEnabled));
      setSelectedDaysOfMonth(first?.selectedDaysOfMonth || []);
      setSelectedMonths(first?.selectedMonths || []);
      setScheduleTimes(
        ag.jobs.length
          ? ag.jobs.map((j) => ({ hours: j.hours, minutes: j.minutes, seconds: j.seconds }))
          : [{ hours: 0, minutes: 0, seconds: 0 }],
      );
      setActionType(first?.actionType || '');
      setToggleAfterSeconds(first?.toggleAfterSeconds || 0);
      setToggleEnabled(Boolean(first?.toggleEnabled));
      
      console.log(`✅ CONFIGURACIONES APLICADAS para ${device.nombre}`);
    } catch (error) {
      console.error(`❌ ERROR cargando configuraciones para ${device.nombre}:`, error);
    }
  }, [device]);

  // Efecto para cargar configuraciones cuando se abre el modal
  useEffect(() => {
    if (open && device) {
      console.log(`📥 CARGANDO configuraciones para ${device.nombre}`);
      loadDeviceSchedules();
    }
  }, [open, device, loadDeviceSchedules]);
  
  // Efecto para cerrar todos los dialogs cuando se cierra el DeviceModal
  useEffect(() => {
    if (!open) {
      // Cerrar todos los dialogs cuando se cierra el modal principal
      setTimerDialogOpen(false);
      setScheduleDialogOpen(false);
      setAgendaDialogOpen(false);
      
      // Guardar configuraciones cuando se cierra el modal
      if (device) {
        saveDeviceSchedules();
      }
    }
  }, [open, device, saveDeviceSchedules]);

  // useEffect para escuchar actualizaciones de dispositivo - EXACTO como DeviceCard
  useEffect(() => {
    if (!device) return;

    const handleDeviceUpdate = (data: any) => {
      if (data.id === device.id) {
        console.log('🔄 [DeviceModal] Actualización recibida:', data);
        console.log('🔄 [DeviceModal] cover_position:', data.cover_position);
        
        // Detectar si hubo un cambio de estado (ON/OFF) antes de actualizar
        const previousEstado = previousEstadoRef.current;
        const newEstado = data.estado;
        const estadoChanged = previousEstado !== undefined && previousEstado !== newEstado;
        
        // Actualizar el ref con el nuevo estado
        previousEstadoRef.current = newEstado;
        
        // LIMPIAR confirmingDevices cuando llega device:update (como en DeviceCard)
        setConfirmingDevices(prev => {
          if (prev.has(device.id)) {
            const newSet = new Set(prev);
            newSet.delete(device.id);
            return newSet;
          }
          return prev;
        });
        
        // Actualizar el dispositivo local con TODOS los campos actualizados
        setLocalDevice(prev => {
          const newDevice = prev ? {
            ...prev,
            consumo: data.consumo ?? 0,
            ultimoConsumoEmitido: data.consumo ?? 0,
            estado: data.estado,
            online: data.online,
            auth_required: data.auth_required ?? prev.auth_required ?? false,
            // Preservar MAC si viene en el evento, sino mantener la existente
            ...(data.mac ? { mac: data.mac } : {}),
            // Preservar IP si viene en el evento, sino mantener la existente
            ...(data.ip ? { ip: data.ip } : {}),
            // Campos especiales según tipo
            ...(data.tipo === 'DIMMER2' && data.lights_brightness !== undefined ? { lights_brightness: data.lights_brightness } : {}),
            ...(data.tipo === 'PLUS2PM' ? { 
              estado: data.estado,
              cover_0_state: data.estado,
              cover_position: data.cover_position, // Agregar cover_position directamente
              metadata: { 
                ...prev.metadata, 
                coverState: data.estado,
                ...(typeof data.cover_position !== 'undefined' ? { cover_position: data.cover_position } : {})
              } 
            } : {})
          } : null;
          
          console.log('🔄 [DeviceModal] Nuevo localDevice:', newDevice);
          return newDevice;
        });
        
        // Si hubo un cambio de estado, recargar el historial de actividades
        // PERO solo si el usuario NO está interactuando (para evitar bloqueos durante la interacción)
        if (estadoChanged) {
          if (isUserInteracting) {
            // Si el usuario está interactuando, marcar que hay un cambio pendiente
            // El historial se recargará cuando el usuario termine de interactuar
            pendingHistoryReloadRef.current = true;
          } else {
            // Si el usuario NO está interactuando, recargar inmediatamente
          // Esperar un momento para que el backend registre el cambio en la BD
          setTimeout(() => {
            loadActivityHistory();
          }, 1000);
          }
        }
      }
    };

    eventBus.on('device:update', handleDeviceUpdate);

    // SINCRONIZACIÓN: Escuchar cambios de timer/agenda de otros usuarios
    const handleSchedulesUpdate = (data: any) => {
      if (!device || data.id !== device.id) return;
      
      console.log('🔄 [DeviceModal] Configuraciones actualizadas por otro usuario:', data);
      
      // Actualizar estados locales con la nueva configuración
      if (data.timer_config) {
        setTimerEnabled(data.timer_config.enabled);
        setAutoOnEnabled(data.timer_config.autoOnEnabled);
        setAutoOffEnabled(data.timer_config.autoOffEnabled);
        setOneTimeOnly(data.timer_config.oneTimeOnly);
        setAutoOnHours(data.timer_config.autoOnHours);
        setAutoOnMinutes(data.timer_config.autoOnMinutes);
        setAutoOnSeconds(data.timer_config.autoOnSeconds);
        setAutoOffHours(data.timer_config.autoOffHours);
        setAutoOffMinutes(data.timer_config.autoOffMinutes);
        setAutoOffSeconds(data.timer_config.autoOffSeconds);
      }
      
      if (data.agenda_config) {
        const ag = normalizeAgendaConfig(data.agenda_config);
        setAgendaEnabled(ag.enabled);
        const first = ag.jobs[0];
        setSelectedDays(first?.selectedDays || []);
        setSpecificDaysEnabled(Boolean(first?.specificDaysEnabled));
        setSpecificMonthsEnabled(Boolean(first?.specificMonthsEnabled));
        setSelectedDaysOfMonth(first?.selectedDaysOfMonth || []);
        setSelectedMonths(first?.selectedMonths || []);
        setScheduleTimes(
          ag.jobs.length
            ? ag.jobs.map((j) => ({ hours: j.hours, minutes: j.minutes, seconds: j.seconds }))
            : [{ hours: 0, minutes: 0, seconds: 0 }],
        );
        setActionType(first?.actionType || '');
        setToggleAfterSeconds(first?.toggleAfterSeconds || 0);
        setToggleEnabled(Boolean(first?.toggleEnabled));
      }
      
      // CRÍTICO: Actualizar localDevice para que PowerButton reciba la config actualizada
      setLocalDevice(prev => prev ? {
        ...prev,
        timer_config: data.timer_config,
        agenda_config: data.agenda_config
      } : null);
      
      console.log('✅ [DeviceModal] UI y localDevice actualizados con nuevas configuraciones');
    };

    eventBus.on('device:schedules_updated', handleSchedulesUpdate);

    return () => {
      eventBus.off('device:update', handleDeviceUpdate);
      eventBus.off('device:schedules_updated', handleSchedulesUpdate);
    };
  }, [device, localDevice, loadActivityHistory, isUserInteracting]);

  // useEffect para recargar el historial cuando el usuario termine de interactuar
  useEffect(() => {
    // Si el usuario terminó de interactuar Y hay un cambio pendiente, recargar el historial
    if (!isUserInteracting && pendingHistoryReloadRef.current) {
      // Esperar un momento para que el backend registre el cambio en la BD
      const timeoutId = setTimeout(() => {
        loadActivityHistory();
        pendingHistoryReloadRef.current = false; // Limpiar el flag
      }, 1000);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isUserInteracting, loadActivityHistory]);

  // useEffect para escuchar timer_master_disabled
  useEffect(() => {
    console.log('🔍 FRONTEND - useEffect timer_master_disabled ejecutándose');
    console.log('🔍 FRONTEND - device:', device);
    
    if (!device) {
      console.log('🔍 FRONTEND - No hay device, saliendo del useEffect');
      return;
    }

    console.log('🔍 FRONTEND - Registrando handler timer_master_disabled para device:', device.id);

    const handleTimerMasterDisabled = (data: any) => {
      console.log('🔍 FRONTEND - timer_master_disabled recibido:', data);
      console.log('🔍 FRONTEND - device.id actual:', device?.id);
      if (data.device_id === device.id) {
        console.log('🔍 FRONTEND - Coincide device_id, actualizando UI');
        
        // SOLUCIÓN AGRESIVA: Múltiples intentos de actualización
        setTimerEnabled(false);
        console.log('🔄 Timer Master switch deshabilitado automáticamente (ONE_TIME_ONLY)');
        
        // FORZAR RE-RENDER con múltiples setTimeout
        setTimeout(() => {
          console.log('🔍 FRONTEND - Forzando re-render #1');
          setTimerEnabled(false);
        }, 0);
        
        setTimeout(() => {
          console.log('🔍 FRONTEND - Forzando re-render #2');
          setTimerEnabled(false);
        }, 10);
        
        setTimeout(() => {
          console.log('🔍 FRONTEND - Forzando re-render #3');
          setTimerEnabled(false);
        }, 50);
        
        // FORZAR RE-RENDER del componente completo
        setTimeout(() => {
          console.log('🔍 FRONTEND - Forzando re-render completo del componente');
          // setDevice no está disponible en este contexto
        }, 100);
        
      } else {
        console.log('🔍 FRONTEND - NO coincide device_id, ignorando evento');
      }
    };

    eventBus.on('timer_master_disabled', handleTimerMasterDisabled);
    console.log('🔍 FRONTEND - Handler timer_master_disabled registrado exitosamente');

    return () => {
      console.log('🔍 FRONTEND - Desregistrando handler timer_master_disabled');
      eventBus.off('timer_master_disabled', handleTimerMasterDisabled);
    };
  }, [device]);

  // Efecto para abrir diálogos cuando se debe
  useEffect(() => {
    if (shouldOpenTimerDialog) {
      setTimerDialogOpen(true);
      setShouldOpenTimerDialog(false);
    }
  }, [shouldOpenTimerDialog]);

  useEffect(() => {
    if (shouldOpenAgendaDialog) {
      setAgendaDialogOpen(true);
      setShouldOpenAgendaDialog(false);
    }
  }, [shouldOpenAgendaDialog]);

  // Efecto para manejar el botón back cuando el modal está abierto
  useEffect(() => {
    if (!open || !onBack) return;

    // Ya no agregamos pushState aquí - se hace en handleDeviceClick de App.tsx

    const handleBack = () => {
      
      // PRIORIDAD 2: Si hay dialogs abiertos, cerrarlos
      if (timerDialogOpen || agendaDialogOpen || scheduleDialogOpen || customDateDialogOpen) {
        // Cerrar todos los dialogs
        setTimerDialogOpen(false);
        setAgendaDialogOpen(false);
        setScheduleDialogOpen(false);
        setCustomDateDialogOpen(false);
      } else {
        // No hay dialogs ni dropdowns, cerrar modal y navegar hacia atrás
        // Eliminar la función global antes de cerrar
        delete (window as any).deviceModalHandleBack;
        
        // Usar onBack en lugar de onClose para navegar hacia atrás
        if (onBack) {
          onBack();
        } else {
          onClose();
        }
      }
    };

    // Exponer la función handleBack para que App.tsx pueda llamarla
    (window as any).deviceModalHandleBack = handleBack;

    return () => {
      delete (window as any).deviceModalHandleBack;
    };
  }, [open, onBack, onClose, timerDialogOpen, agendaDialogOpen, scheduleDialogOpen, customDateDialogOpen]);

  // MEJORA: Sincronizar isUserInteractingRef con el estado
  useEffect(() => {
    isUserInteractingRef.current = isUserInteracting;
  }, [isUserInteracting]);

  // MEJORA: Inicializar valores de sliders cuando se abre el modal o cambia el dispositivo
  useEffect(() => {
    if (device && open) {
      // Inicializar brillo para DIMMER2
      if (device.tipo === 'DIMMER2' && device.lights_brightness !== undefined) {
        setCurrentBrightness(device.lights_brightness);
        setLocalBrightness(device.lights_brightness);
      }
      
      // Inicializar apertura para PLUS2PM
      if (device.tipo === 'PLUS2PM' && device.cover_position !== undefined) {
        setCurrentAperture(device.cover_position);
        setLocalAperture(device.cover_position);
        setInitialAperture(device.cover_position);
      }
    }
  }, [device, open]); // Solo cuando cambia el dispositivo o se abre el modal

  useEffect(() => {
    // Solo sincronizar si el usuario NO está interactuando
    if (localDevice?.lights_brightness !== undefined && !isUserInteractingRef.current) {
      // Solo actualizar si el valor es diferente para evitar re-renders innecesarios
      const newBrightness = localDevice.lights_brightness;
      setCurrentBrightness(prev => {
        if (prev !== newBrightness) {
          return newBrightness;
        }
        return prev;
      });
      setLocalBrightness(prev => {
        if (prev !== newBrightness) {
          return newBrightness;
        }
        return prev;
      });
    }
  }, [localDevice?.lights_brightness, device]);

  // MEJORA: Efecto para sincronizar currentAperture con los cambios del dispositivo
  // SOLO cuando el usuario NO está interactuando con el slider
  useEffect(() => {
    // Solo sincronizar si el usuario NO está interactuando
    if (localDevice?.cover_position !== undefined && !isUserInteractingRef.current) {
      // Solo actualizar si el valor es diferente para evitar re-renders innecesarios
      const newAperture = localDevice.cover_position;
      setCurrentAperture(prev => {
        if (prev !== newAperture) {
          return newAperture;
        }
        return prev;
      });
      setLocalAperture(prev => {
        if (prev !== newAperture) {
          return newAperture;
        }
        return prev;
      });
    }
  }, [localDevice?.cover_position, device]);


  // Funciones para determinar si hay configuración por defecto
  const isTimerDefault = () => {
    return !autoOnEnabled && !autoOffEnabled && !oneTimeOnly;
  };

  // Funciones para manejar múltiples horarios
  const addScheduleTime = () => {
    if (scheduleTimes.length < 48) {
      setScheduleTimes([...scheduleTimes, {hours: 0, minutes: 0, seconds: 0}]);
    }
  };

  const removeScheduleTime = (index: number) => {
    if (scheduleTimes.length > 1) {
      setScheduleTimes(scheduleTimes.filter((_, i) => i !== index));
    }
  };

  const updateScheduleTime = (index: number, field: 'hours' | 'minutes' | 'seconds', value: number) => {
    const newTimes = [...scheduleTimes];
    newTimes[index] = { ...newTimes[index], [field]: value };
    setScheduleTimes(newTimes);
  };
  
  // Stable callback functions to prevent re-renders
  const handleAutoOnHoursChange = useCallback((value: number) => {
    setAutoOnHours(value);
  }, []);

  const handleAutoOnMinutesChange = useCallback((value: number) => {
    setAutoOnMinutes(value);
  }, []);

  const handleAutoOnSecondsChange = useCallback((value: number) => {
    setAutoOnSeconds(value);
  }, []);

  const handleAutoOffHoursChange = useCallback((value: number) => {
    setAutoOffHours(value);
  }, []);

  const handleAutoOffMinutesChange = useCallback((value: number) => {
    setAutoOffMinutes(value);
  }, []);

  const handleAutoOffSecondsChange = useCallback((value: number) => {
    setAutoOffSeconds(value);
  }, []);
  
  const handleAgendaClose = useCallback(() => {
    setAgendaDialogOpen(false);
    setAgendaStep(1);
  }, []);
  
  const handleAgendaNext = useCallback(() => {
    if (agendaStep < 4) {
      setAgendaStep(agendaStep + 1);
    }
  }, [agendaStep]);
  
  const handleAgendaBack = useCallback(() => {
    if (agendaStep > 1) {
      setAgendaStep(agendaStep - 1);
    }
  }, [agendaStep]);
  
  const handleAgendaSave = useCallback(async () => {
    const currentUser = getUser();
    const isDemoUser = currentUser && isDemo(currentUser);

    if (isDemoUser) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'save_agenda_button',
        device?.id || null,
        device ? `Guardar agenda: ${device.nombre}` : 'Guardar agenda',
        {
          screen: 'device_modal',
          deviceId: device?.id || null,
          attemptedAction: 'save_agenda',
          blocked: true,
          blockReason: 'demo_user_no_configure'
        }
      );
      return;
    }

    try {
      await saveAgendaSchedules();
      setAgendaDialogOpen(false);
      setAgendaStep(1);
    } catch (error: any) {
      throw error;
    }
  }, [saveAgendaSchedules, device, t]);

  // Función para resetear solo Timer a valores por defecto
  const resetTimerToDefault = useCallback(() => {
    console.log('🔄 Reseteando Timer a valores por defecto');
    setAutoOnEnabled(false);
    setAutoOffEnabled(false);
    setOneTimeOnly(false);
    setAutoOnHours(0);
    setAutoOnMinutes(0);
    setAutoOnSeconds(0);
    setAutoOffHours(0);
    setAutoOffMinutes(0);
    setAutoOffSeconds(0);
    console.log('✅ Timer reseteado a valores por defecto');
  }, []);

  // Función para resetear solo Agenda a valores por defecto
  const resetAgendaToDefault = useCallback(() => {
    console.log('🔄 Reseteando Agenda a valores por defecto');
    setSelectedDays([]);
    setSpecificDaysEnabled(false);
    setSpecificMonthsEnabled(false);
    setSelectedDaysOfMonth([]);
    setSelectedMonths([]);
    setScheduleTimes([{ hours: 0, minutes: 0, seconds: 0 }]);
    setActionType('');
    setToggleAfterSeconds(0);
    setToggleEnabled(false);
    setTimeError('');
    setDurationError('');
    console.log('✅ Agenda reseteada a valores por defecto');
  }, []);
  
  // Funciones para barra de brillo DIMMER2
  
  const handleBrightnessChange = useCallback((event: Event, newValue: number | number[]) => {
    // Verificar si el usuario es demo
    const currentUser = getUser();
    const isDemoUser = currentUser && isDemo(currentUser);
    if (isDemoUser) {
      // Tracking para usuarios Demo
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'brightness_change',
        device?.id || null,
        `Cambiar brillo: ${newValue}%`,
        {
          screen: 'device_modal',
          deviceId: device?.id || null,
          attemptedAction: 'change_brightness',
          blocked: true,
          blockReason: 'demo_user_no_control',
          value: newValue
        }
      );
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      return;
    }
    
    // Verificar permisos antes de permitir cambios
    if (!canControl) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.noControlDevices')
      });
      return;
    }
    
    // MEJORA: Marcar que el usuario está interactuando INMEDIATAMENTE para evitar interferencia de telemetría
    setIsUserInteracting(true);
    isUserInteractingRef.current = true;
    // Actualizar tanto el estado local como el valor mostrado en tiempo real
    const newBrightness = newValue as number;
    setLocalBrightness(newBrightness);
    setCurrentBrightness(newBrightness); // Mostrar el cambio en tiempo real
  }, [canControl, device, t]);
  
  const handleBrightnessChangeCommitted = useCallback((event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    // Verificar si el usuario es demo
    const currentUser = getUser();
    const isDemoUser = currentUser && isDemo(currentUser);
    if (isDemoUser) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      return;
    }
    // Verificar permisos antes de enviar comando
    if (!canControl) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.noControlDevices')
      });
      return;
    }
    
    // Enviar comando inmediatamente sin delay
    const finalValue = newValue as number;
    setCurrentBrightness(finalValue);
    setLocalBrightness(finalValue);
    
    // Enviar comando al dispositivo (brightness no usa PIN por ahora, se controla en el toggle)
    if (device && device.tipo === 'DIMMER2') {
      const WebSocketService = (window as any).WebSocketService;
      if (WebSocketService) {
        // MEJORA: Asignar listener ANTES de enviar comando para asegurar que esté listo
        const listenerId = WebSocketService.assignListener(device.id);
        if (listenerId !== null) {
          WebSocketService.sendDeviceControl(device.id, 'set_brightness', finalValue);
        }
      }
    }
    
    // MEJORA: Después de un delay más largo, permitir que la telemetría vuelva a sincronizar
    // Aumentar el delay para dar tiempo a que el servidor procese y responda
    setTimeout(() => {
      setIsUserInteracting(false);
      isUserInteractingRef.current = false;
    }, 3000); // 3 segundos de delay para evitar interferencia
  }, [device, canControl, t]);
  
  // Funciones para control de apertura PLUS2PM
  
  const handleApertureChange = useCallback((event: Event, newValue: number | number[]) => {
    // Tracking para usuarios Demo
    const currentUser = getUser();
    if (currentUser && isDemo(currentUser)) {
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'aperture_change',
        device?.id || null,
        `Cambiar apertura: ${newValue}%`,
        {
          screen: 'device_modal',
          deviceId: device?.id || null,
          attemptedAction: 'change_aperture',
          blocked: true,
          blockReason: 'demo_user_no_control',
          value: newValue
        }
      );
    }
    
    // Verificar permisos antes de permitir cambios
    if (!canControl) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.noControlDevices')
      });
      return;
    }
    
    // MEJORA: Marcar que el usuario está interactuando INMEDIATAMENTE para evitar interferencia de telemetría
    setIsUserInteracting(true);
    isUserInteractingRef.current = true;
    // Actualizar tanto el estado local como el valor mostrado en tiempo real
    const newAperture = newValue as number;
    
    // Capturar el valor inicial solo la primera vez
    if (initialAperture === 50 && newAperture !== 50) {
      setInitialAperture(currentAperture);
    }
    
    setLocalAperture(newAperture);
    setCurrentAperture(newAperture); // Mostrar el cambio en tiempo real
    
    // Activar animación de los botones basada en la dirección del cambio
    const deviceId = (localDevice || device)?.id;
    if (deviceId && newAperture > currentAperture) {
      // Movimiento hacia arriba (abriendo)
      setCoverActions(prev => ({ ...prev, [deviceId]: 'up' }));
    } else if (deviceId && newAperture < currentAperture) {
      // Movimiento hacia abajo (cerrando)
      setCoverActions(prev => ({ ...prev, [deviceId]: 'down' }));
    }
  }, [currentAperture, initialAperture, localDevice, device, canControl, t]);
  
  const handleApertureChangeCommitted = useCallback((event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    // Verificar permisos antes de enviar comando
    if (!canControl) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.noControlDevices')
      });
      return;
    }
    
    // Enviar comando inmediatamente sin delay
    const finalValue = newValue as number;
    
    // Detener animaciones de los botones cuando se suelta el slider
    const deviceId = (localDevice || device)?.id;
    if (deviceId) {
      setCoverActions(prev => ({ ...prev, [deviceId]: 'stop' }));
    }
    
    // Actualizar estados locales INMEDIATAMENTE para evitar rebote
    setCurrentAperture(finalValue);
    setLocalAperture(finalValue);
    
    // Marcar que el usuario está interactuando para evitar interferencia de telemetría
    setIsUserInteracting(true);
    
    // Determinar la dirección del movimiento basado en el valor inicial
    const previousValue = initialAperture; // Usar el valor inicial capturado
    const isOpening = finalValue > previousValue;
    const isClosing = finalValue < previousValue;
    
    // Simular el comportamiento de los botones para feedback visual INMEDIATAMENTE
    if (deviceId) {
      if (isOpening) {
        // Simular que se presionó el botón de subir
        setCoverActions(prev => ({
          ...prev,
          [deviceId]: 'up'
        }));
        
        // Después de un tiempo, simular que se detiene
        setTimeout(() => {
          setCoverActions(prev => ({
            ...prev,
            [deviceId]: null
          }));
        }, 3000); // 3 segundos de animación
        
      } else if (isClosing) {
        // Simular que se presionó el botón de bajar
        setCoverActions(prev => ({
          ...prev,
          [deviceId]: 'down'
        }));
        
        // Después de un tiempo, simular que se detiene
        setTimeout(() => {
          setCoverActions(prev => ({
            ...prev,
            [deviceId]: null
          }));
        }, 3000); // 3 segundos de animación
      }
    }

    // Actualizar estados locales INMEDIATAMENTE para evitar rebote
    setCurrentAperture(finalValue);
    setLocalAperture(finalValue);
    
    // Marcar que el usuario está interactuando para evitar interferencia de telemetría
    setIsUserInteracting(true);

    // Enviar comando al dispositivo
    if (device && device.tipo === 'PLUS2PM') {
      const WebSocketService = (window as any).WebSocketService;
      if (WebSocketService) {
        // MEJORA: Asignar listener ANTES de enviar comando para asegurar que esté listo
        const listenerId = WebSocketService.assignListener(device.id);
        if (listenerId !== null) {
          WebSocketService.sendDeviceControl(device.id, 'set_aperture', finalValue);
        }
      }
    }
    
    // Resetear el valor inicial para la próxima interacción
    setInitialAperture(finalValue);
    
    // MEJORA: Resetear isUserInteracting después de un delay más largo para permitir actualizaciones del servidor
    // Aumentar el delay para dar tiempo a que el servidor procese y responda
    setTimeout(() => {
      console.log('🔄 [DeviceModal] Reseteando isUserInteracting a false');
      setIsUserInteracting(false);
      isUserInteractingRef.current = false;
    }, 3000); // 3 segundos de delay para permitir que llegue la respuesta del servidor
  }, [device, canControl, localDevice, initialAperture, t]);
  
  // Función para manejar el toggle del dispositivo - EXACTO como DeviceCard
  const handleToggleDevice = useCallback(() => {
    if (!device) return;
    
    // MEJORA: Verificaciones rápidas primero (sin async)
    if (userIsDemo || !canControl) {
      setPermissionSnackbar({
        open: true,
        message: userIsDemo
          ? t('permissions.demoRestriction')
          : t('permissions.noControlDevices')
      });
      return;
    }
    
    // MEJORA: Activar feedback visual INMEDIATAMENTE para deshabilitar el botón
    // Esto previene múltiples clics mientras se procesa el comando
    const deviceId = device.id;
    setConfirmingDevices(prev => new Set(prev).add(deviceId));
    
    // Verificar PIN antes de toggle
    if ((window as any).checkPinRequired && (window as any).checkPinRequired(device)) {
      if (!(window as any).isPinSessionValid || !(window as any).isPinSessionValid()) {
        // Si requiere PIN y no está validado, limpiar confirmingDevices antes de abrir el dialog
        setConfirmingDevices(prev => {
          const newSet = new Set(prev);
          newSet.delete(deviceId);
          return newSet;
        });
        (window as any).requestPinForControl(device, 'toggle');
        return;
      }
    }
    
    // Marcar que el usuario está interactuando para evitar recargas del historial durante la interacción
    setIsUserInteracting(true);
    
    // Usar la función de toggle recibida por props
    // Esta función manejará el resto del flujo (asignación de listener, envío de comando, etc.)
    if (onDeviceToggle && device) {
      onDeviceToggle(device);
    } else {
      // Si no hay onDeviceToggle, limpiar confirmingDevices
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
    
    // Resetear el flag después de un delay para permitir que llegue la respuesta del servidor
    setTimeout(() => {
      setIsUserInteracting(false);
    }, 2000); // 2 segundos de delay
  }, [device, onDeviceToggle, userIsDemo, canControl, t]);
  
  // Función para sincronizar el slider con los botones
  const handleApertureUpdateFromButtons = useCallback((position: number) => {
    console.log('🔄 [DeviceModal] handleApertureUpdateFromButtons llamado con:', position);
    console.log('🔄 [DeviceModal] isUserInteracting:', isUserInteracting);
    
    // Solo actualizar si el usuario NO está interactuando con el slider
    if (!isUserInteracting) {
      console.log('🔄 [DeviceModal] Actualizando slider con posición:', position);
      setCurrentAperture(position);
      setLocalAperture(position);
    } else {
      console.log('🔄 [DeviceModal] Usuario interactuando, no actualizando slider');
    }
  }, [isUserInteracting]);

  // Función para controlar persianas (PLUS2PM)
  const handleControlCover = useCallback(async (deviceId: number, action: 'up' | 'down' | 'stop') => {
    if (userIsDemo || !canControl) {
      setPermissionSnackbar({
        open: true,
        message: userIsDemo
          ? t('permissions.demoRestriction')
          : t('permissions.noControlDevices')
      });
      return;
    }
    try {
      // Marcar que el usuario está interactuando para evitar recargas del historial durante la interacción
      setIsUserInteracting(true);
      
      // FEEDBACK VISUAL INMEDIATO - Activar estado de confirmación
      setConfirmingDevices(prev => new Set(prev).add(deviceId));

      // ACTUALIZAR coverActions INMEDIATAMENTE para feedback visual
      setCoverActions(prev => {
        const newActions = { ...prev };
        if (action === 'stop') {
          // Si es stop, limpiar la acción
          delete newActions[deviceId];
        } else {
          // Si es up/down, establecer la acción
          newActions[deviceId] = action;
        }
        return newActions;
      });

      // Guardar la acción para establecer/limpiar coverActions cuando llegue confirmación del backend
      setPendingCoverActions(prev => ({ ...prev, [deviceId]: action }));
      
      // Configurar timeout para detectar falta de respuesta (9 segundos)
      const timeoutId = setTimeout(() => {
        // Verificar INMEDIATAMENTE si aún está esperando confirmación
        setConfirmingDevices(prev => {
          if (prev.has(deviceId)) {
            console.log(`⏰ [COVER] Timeout para dispositivo ${deviceId} - limpiando estado de confirmación`);
            const newSet = new Set(prev);
            newSet.delete(deviceId);
            return newSet;
          }
          return prev;
        });
        
        // Limpiar la acción pendiente
        setPendingCoverActions(prev => {
          const newPending = { ...prev };
          delete newPending[deviceId];
          return newPending;
        });
      }, 9000);

      // Verificar PIN antes de controlar cover
      const deviceForPin = (window as any).mapaDispositivos?.[deviceId];
      if (deviceForPin && (window as any).checkPinRequired && (window as any).checkPinRequired(deviceForPin)) {
        if (!(window as any).isPinSessionValid || !(window as any).isPinSessionValid()) {
          (window as any).requestPinForControl(deviceForPin, 'cover', action);
          return;
        }
      }

      // Enviar comando al dispositivo
      if (action === 'stop') {
        // Usar la función de control de covers recibida por props
        if (onControlCover) {
          onControlCover(deviceId, 'stop');
        }
      } else {
        // Usar la función de control de covers recibida por props
        if (onControlCover) {
          onControlCover(deviceId, action);
        }
      }

      // Resetear el flag después de un delay para permitir que llegue la respuesta del servidor
      setTimeout(() => {
        setIsUserInteracting(false);
      }, 2000); // 2 segundos de delay

      // Limpiar timeout cuando se reciba confirmación
      const cleanup = () => {
        clearTimeout(timeoutId);
      };
      
      // Retornar función de limpieza
      return cleanup;
    } catch (error) {
      console.error('❌ [COVER] Error al controlar persiana:', error);
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
      // Resetear el flag incluso si hay error
      setIsUserInteracting(false);
    }
  }, [userIsDemo, canControl, onControlCover, t]);

  // Actualizar favorito cuando device cambie
  useEffect(() => {
    if (device) {
      setIsFavorite(device.is_favorite || false);
    }
  }, [device]);
  
  if (!device) return null;

  // Manejar el cierre con transición
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  // Handler para toggle de favorito
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Tracking para usuarios Demo
    const currentUser = getUser();
    if (currentUser && isDemo(currentUser)) {
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'toggle_favorite_in_modal',
        device?.id || null,
        device ? `Toggle favorito: ${device.nombre}` : 'Toggle favorito',
        {
          screen: 'device_modal',
          deviceId: device?.id || null,
          attemptedAction: 'toggle_favorite',
          blocked: true,
          blockReason: 'demo_user_no_edit'
        }
      );
    }
    
    if (userIsDemo) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      return;
    }
    
    console.log('[FAVORITO] 🔄 Handler ejecutado - VERSIÓN ACTUALIZADA');
    
    if (!device) {
      return;
    }
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = userData.id;
      
      console.log('[FAVORITO] 📝 userData:', userData);
      console.log('[FAVORITO] 👤 userId:', userId);
      
      if (!userId) {
        console.error('[FAVORITO] ❌ Usuario no identificado');
        return;
      }
      
      // Calcular el NUEVO estado primero
      const newFavoriteState = !isFavorite;
      
      console.log('[FAVORITO] 🔄 Estado actual:', isFavorite, '→ Nuevo estado:', newFavoriteState);
      
      // Actualizar el estado local INMEDIATAMENTE para feedback visual
      setIsFavorite(newFavoriteState);
      console.log('[FAVORITO] ✅ setIsFavorite llamado con:', newFavoriteState);
      
      // Actualizar el device prop directamente (mutable)
      if (device) {
        device.is_favorite = newFavoriteState;
        console.log('[FAVORITO] ✅ device.is_favorite actualizado a:', newFavoriteState);
      }
      
      // Actualizar mapaDispositivos con el NUEVO valor
      if (mapaDispositivos[device.id]) {
        mapaDispositivos[device.id].is_favorite = newFavoriteState;
        console.log('[FAVORITO] ✅ mapaDispositivos actualizado');
      }
      
      // Llamar al API (en background, sin await para mejor UX)
      console.log('[FAVORITO] 📡 Llamando API...');
      if (newFavoriteState) {
        addFavorite(userId, device.id)
          .then(() => {
            console.log('[FAVORITO] ✅ API: Favorito agregado exitosamente');
            // Actualizar también mapaDispositivos.is_favorite
            if (mapaDispositivos[device.id]) {
              mapaDispositivos[device.id].is_favorite = true;
            }
          })
          .catch(error => {
            console.error('[FAVORITO] ❌ Error agregando favorito:', error);
            console.error('[FAVORITO] ❌ Error detalles:', error.response?.data);
            // Revertir en caso de error
            setIsFavorite(false);
            if (device) device.is_favorite = false;
            if (mapaDispositivos[device.id]) mapaDispositivos[device.id].is_favorite = false;
          });
      } else {
        removeFavorite(userId, device.id)
          .then(() => {
            console.log('[FAVORITO] ✅ API: Favorito eliminado exitosamente');
            // removeFavorite ya actualiza mapaDispositivos.is_favorite
          })
          .catch(error => {
            console.error('[FAVORITO] ❌ Error eliminando favorito:', error);
            console.error('[FAVORITO] ❌ Error detalles:', error.response?.data);
            // Revertir en caso de error
            setIsFavorite(true);
            if (device) device.is_favorite = true;
            if (mapaDispositivos[device.id]) mapaDispositivos[device.id].is_favorite = true;
          });
      }
      
      // Emitir evento para refrescar vista principal
      eventBus.emit('favoritos:actualizado');
      console.log('[FAVORITO] 📡 Evento emitido');
      
    } catch (error) {
      console.error('[FAVORITO] ❌ Error toggling favorite:', error);
    }
  };


  // Determinar el estado del dispositivo
  const currentDevice = localDevice || device;
  const isOn = currentDevice?.tipo === 'PLUS2PM' 
    ? currentDevice.cover_0_state === 'opening' || currentDevice.cover_0_state === 'closing'
    : currentDevice?.tipo === 'DIMMER2'
    ? (currentDevice.estado === true || (typeof currentDevice.estado === 'number' && currentDevice.estado > 0))
    : Boolean(currentDevice?.estado);

  const modalTipo = String(currentDevice?.tipo || '');
  const showBrightnessSlider =
    modalTipo === 'DIMMER2' ||
    modalTipo.toLowerCase() === 'dimmer2' ||
    currentDevice?.lights_brightness !== undefined;
  const showApertureSlider = modalTipo === 'PLUS2PM';
  const showSideSlider = showBrightnessSlider || showApertureSlider;


  // Manejar swipe hacia la derecha para cerrar.
  // No enganchar el gesto si el toque empieza en un control: un listener de
  // touchmove en document se come el click del botón de encendido en móvil.
  const handleTouchStart = (event: React.TouchEvent) => {
    const origin = event.target as HTMLElement | null;
    if (origin?.closest('button, a, input, textarea, select, [role="button"], .MuiSlider-root, .MuiSwitch-root, .MuiIconButton-root')) {
      return;
    }
    const touch = event.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      const currentX = moveEvent.touches[0].clientX;
      const currentY = moveEvent.touches[0].clientY;
      const deltaX = currentX - startX;
      const deltaY = Math.abs(currentY - startY);
      
      // Si el swipe es hacia la derecha y es más horizontal que vertical, cerrar el modal
      if (deltaX > 100 && deltaX > deltaY) {
        handleClose();
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
  };

  // En horizontal, renderizar como contenido simple (sin backdrop ni modal completo)
  if (isHorizontal) {
    if (!open) return null;
    return (
      <Box
        data-testid="device-modal"
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#000',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Slide
          direction="left"
          in={open && !isClosing}
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#000',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 0,
                boxShadow: 'none',
                border: 'none',
              }}
            >
          {/* Header con nombre del dispositivo y botón cerrar - reducido 15% altura y letra */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              py: 0.55,
              borderBottom: `1px solid ${SURFACE_BORDER}`,
              background: 'linear-gradient(180deg, #1c1c1c 0%, #141414 100%)',
              minHeight: 36,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '0.82rem',
                textTransform: 'capitalize',
                textAlign: 'center',
                flex: 1,
                lineHeight: 1.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 0.5,
              }}
            >
              {(localDevice || device)?.nombre}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                padding: '4px',
                flexShrink: 0,
                transition: PRESS_TRANSITION,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
                '&:active': { transform: 'scale(0.9)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Contenido principal */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto', // Permitir scroll en el contenido
              p: 1.5,
              pt: 0.85, // Espacio entre título y primer recuadro = 0.85
              background: 'radial-gradient(120% 60% at 50% -10%, rgba(35,145,255,0.08) 0%, transparent 50%)',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'black',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: ACCENT,
                borderRadius: '3px',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#2391FF black',
            }}
          >
            {/* 1. Gráfico de consumo */}
            <Card
              sx={{
                ...CHART_CARD_SX,
                mb: 0.85, // Espacio entre recuadros = 0.85
                p: 1.5,
                pt: 0.75, // Reducido padding superior a la mitad
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <TimelineIcon sx={{ color: ACCENT, mr: 1, fontSize: '1.05rem', opacity: 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={SECTION_TITLE_SX}
                >
                  {t('deviceModal.consumptionGraph')}
                </Typography>
              </Box>
              
              {/* Separación entre título y filtros */}
              <Box sx={{ height: '2px', mb: 0 }} />
              
              {/* Filtros - Primer renglón (igual que HistoricalReports) */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'nowrap', mb: 1.5, mt: 0.25, pr: 0.25, minWidth: 0 }}>
                <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                  <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.period')}</InputLabel>
                  <Select
                    value={period}
                    onChange={(e) => handlePeriodChange(e.target.value as PeriodOption)}
                    label={t('deviceModal.period')}
                    sx={{ 
                      color: '#fff', 
                      fontSize: 12,
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' }, 
                      '& .MuiSvgIcon-root': { color: MUTED, fontSize: 18 }
                    }}
                    MenuProps={{
                      style: CHART_MENU_LAYER_STYLE,
                      PaperProps: {
                        sx: MENU_PAPER_SX
                      }
                    }}
                  >
                    <MenuItem value="1hour">{t('reports.periods.1hour')}</MenuItem>
                    <MenuItem value="6hours">{t('reports.periods.6hours')}</MenuItem>
                    <MenuItem value="12hours">{t('reports.periods.12hours')}</MenuItem>
                    <MenuItem value="24hours">{t('reports.periods.24hours')}</MenuItem>
                    <MenuItem value="7days">{t('reports.periods.7days')}</MenuItem>
                    <MenuItem value="30days">{t('reports.periods.30days')}</MenuItem>
                    <MenuItem value="12months">{t('reports.periods.12months')}</MenuItem>
                    <MenuItem value="today">{t('reports.periods.today')}</MenuItem>
                    <MenuItem value="yesterday">{t('reports.periods.yesterday')}</MenuItem>
                    <MenuItem value="thisWeek">{t('reports.periods.thisWeek')}</MenuItem>
                    <MenuItem value="thisMonth">{t('reports.periods.thisMonth')}</MenuItem>
                    <MenuItem value="thisYear">{t('reports.periods.thisYear')}</MenuItem>
                    <MenuItem value="billing">{t('reports.periods.billing')}</MenuItem>
                    <MenuItem value="custom">{t('reports.periods.custom')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
                  <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.interval')}</InputLabel>
                  <Select
                    value={interval}
                    onChange={(e) => {
                      const newInterval = e.target.value as IntervalOption;
                      // Tracking para usuarios Demo
                      const currentUser = getUser();
                      if (currentUser && isDemo(currentUser)) {
                        const tracker = getDemoActivityTracker();
                        tracker.trackFilterChange('interval', newInterval, {
                          screen: 'device_modal',
                          deviceId: device?.id || null
                        });
                      }
                      setChartInterval(newInterval);
                    }}
                    label={t('deviceModal.interval')}
                    sx={{ 
                      color: '#fff', 
                      fontSize: 12,
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' }, 
                      '& .MuiSvgIcon-root': { color: MUTED, fontSize: 18 }
                    }}
                    MenuProps={{
                      style: CHART_MENU_LAYER_STYLE,
                      PaperProps: {
                        sx: MENU_PAPER_SX
                      }
                    }}
                  >
                    {/* ✅ REFACTORIZACIÓN: Mostrar solo intervalos válidos según período (igual que HistoricalReports) */}
                    {getAvailableIntervals().map(intv => (
                      <MenuItem key={intv} value={intv}>{t(`reports.intervals.${intv}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeviceModalNavigateDay('previous')}
                      aria-label={t('solarEnergy.tab2.dateSelector.previousDay')}
                      sx={{
                        color: '#999',
                        padding: '2px',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <ChevronLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleDeviceModalGoToToday}
                      disabled={period === 'today' && !customStartDate}
                      aria-label={t('solarEnergy.tab2.dateSelector.today')}
                      title={t('solarEnergy.tab2.dateSelector.today')}
                      sx={{
                        color: '#999',
                        padding: '2px',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                        '&.Mui-disabled': { color: '#444' },
                      }}
                    >
                      <TodayIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeviceModalNavigateDay('next')}
                      disabled={!deviceModalCanNavigateNextDay}
                      aria-label={t('solarEnergy.tab2.dateSelector.nextDay')}
                      sx={{
                        color: deviceModalCanNavigateNextDay ? '#999' : '#444',
                        padding: '2px',
                        '&:hover': deviceModalCanNavigateNextDay
                          ? { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
                          : {},
                        '&.Mui-disabled': { color: '#444' },
                      }}
                    >
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  {/* ✅ REFACTORIZACIÓN: Eliminado selector potencia/energía - siempre mostramos potencia en gráfico y energía en leyendas */}
                  <IconButton
                    onClick={() => {
                      setDeviceChartData([]);
                      setDeviceChartDataEnergy([]);
                      loadDeviceChartData();
                    }}
                    sx={{
                      color: '#999',
                      padding: '2px',
                      marginLeft: '-2px',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: '#2391FF',
                      },
                    }}
                    size="small"
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Gráfico de consumo del dispositivo */}
              {loadingChart ? (
                <Box sx={{ height: '145px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} sx={{ color: ACCENT }} />
                </Box>
              ) : formatDeviceChartData.length > 0 ? (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{
                    height: '145px',
                    width: '100%',
                    borderRadius: '14px',
                    bgcolor: 'rgba(0,0,0,0.22)',
                    border: `1px solid ${SURFACE_BORDER}`,
                    px: 0.5,
                    pt: 0.5,
                    pb: 0.5,
                    overflow: 'hidden',
                  }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={formatDeviceChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="4 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis 
                          dataKey="time" 
                          stroke="rgba(255,255,255,0.35)" 
                          tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 9 }} 
                          angle={-45} 
                          textAnchor="end" 
                          height={50}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          stroke="rgba(255,255,255,0.35)" 
                          tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 9 }} 
                          label={{ 
                            value: t('deviceModal.consumption'), // Siempre mostrar consumo (potencia)
                            angle: -90, 
                            position: 'insideLeft', 
                            style: { fill: 'rgba(255,255,255,0.45)', fontSize: 9 } 
                          }}
                          tickFormatter={(value) => {
                            // ✅ REFACTORIZACIÓN: Siempre mostrar potencia en eje Y
                            const absValue = Math.abs(value);
                            if (absValue >= 1000) {
                              return `${(value / 1000).toFixed(1)} kW`;
                            }
                            return `${Math.round(value)} W`;
                          }}
                          width={50}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          allowEscapeViewBox={{ x: false, y: true }}
                          position={{ y: 90 }}
                          offset={-5}
                          wrapperStyle={{ 
                            zIndex: 1000
                          }}
                        />
                        {/* ✅ REFACTORIZACIÓN: Usar Area para mostrar energía debajo de la curva (igual que HistoricalReports) */}
                        <Area
                          type="monotone"
                          dataKey="consumo"
                          stroke="none"
                          fill={ACCENT}
                          fillOpacity={0.22}
                          strokeWidth={0}
                          dot={false}
                          connectNulls={false}
                          baseValue={0}
                          name={device?.nombre || t('deviceModal.consumption')}
                        />
                        {/* ✅ REFACTORIZACIÓN: Usar Line para potencia encima del área (igual que HistoricalReports) */}
                        <Line 
                          type="monotone" 
                          dataKey="consumo" 
                          stroke={ACCENT} 
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls={false}
                          name={device?.nombre || t('deviceModal.consumption')}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  {/* Valor total del período */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: -0.5,
                    mb: 0,
                    height: '20px'
                  }}>
                    <Typography 
                      sx={{ 
                        color: '#00C853', // ✅ REFACTORIZACIÓN: Verde para energía en leyenda
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        lineHeight: 1
                      }}
                    >
                      {t('reports.energy')}: {(() => {
                        // ✅ REFACTORIZACIÓN: Mostrar energía total desde deviceEnergyTotal
                        const energia = deviceEnergyTotal;
                        return Math.abs(energia) >= 1000 
                          ? `${(energia / 1000).toFixed(2)} kWh` 
                          : `${energia.toFixed(2)} Wh`;
                      })()}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '145px',
                    backgroundColor: '#000',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #444',
                  }}
                >
                  <Typography sx={{ color: '#999', fontSize: 12, textAlign: 'center' }}>
                    No hay datos disponibles para el período seleccionado
                  </Typography>
                </Box>
              )}
            </Card>

            {/* 2. Historial de actividades */}
            <Card
              sx={{
                ...CARD_SURFACE_SX,
                mb: 0.85, // Espacio entre recuadros = 0.85
                p: 1.221759375, // Reducido 5% más de 1.2860625 a 1.221759375
                pt: 0.6108796875, // Reducido 5% más de 0.64303125 a 0.6108796875
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}> {/* Reducido significativamente */}
                <HistoryIcon sx={{ color: ACCENT, mr: 1, fontSize: '1.05rem', opacity: 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={SECTION_TITLE_SX}
                >
                  {t('deviceModal.activityHistory')}
                </Typography>
              </Box>

              <Box sx={{ 
                height: '91px', // +18px desde Timer+Agenda (sin hueco bajo chips)
                overflowY: 'auto',
                // Scrollbar fino y transparente
                '&::-webkit-scrollbar': {
                  width: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#333',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#2391FF',
                  borderRadius: '2px',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#2391FF #333',
              }}>
                {loadingActivityHistory ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                    <CircularProgress size={16} sx={{ color: ACCENT }} />
                  </Box>
                ) : activityHistory.length > 0 ? (
                  activityHistory.map((state, index) => {
                    // ✅ Usar fecha y hora ya formateadas desde el backend (GMT-3)
                    // El backend envía 'fecha' y 'hora' ya formateadas en GMT-3
                    const fecha = state.fecha || (() => {
                      const timestamp = new Date(state.timestamp);
                      return formatInServerTz(timestamp, 'yyyy-MM-dd');
                    })();
                    const hora = state.hora || (() => {
                      const timestamp = new Date(state.timestamp);
                      return formatInServerTz(timestamp, 'HH:mm');
                    })();
                    const estado = state.estado === true || state.estado === 'on' || state.estado === 'ON' || state.estado === 'true';
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0,
                          height: 17,
                          minHeight: 17,
                          maxHeight: 17,
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Columna 1: Fecha con indentación */}
                        <Box sx={{ flex: '0 0 auto', ml: 2.5, mr: 6, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '0.6rem',
                            lineHeight: 1,
                          }}>
                            {formatDate(fecha)}
                          </Typography>
                        </Box>
                        
                        {/* Columna 2: Hora */}
                        <Box sx={{ flex: '0 0 auto', mr: 12, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '0.6rem',
                            lineHeight: 1,
                          }}>
                            {hora} hs.
                          </Typography>
                        </Box>
                        
                        {/* Columna 3: Chip ON/OFF */}
                        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={estado ? t('deviceModal.on') : t('deviceModal.off')}
                            size="small"
                            sx={{
                              backgroundColor: estado ? '#00ff00' : '#ff4444',
                              color: 'black',
                              fontWeight: 'bold',
                              fontSize: '0.38rem',
                              height: '11px',
                              width: '28px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              '& .MuiChip-label': {
                                px: 0.4,
                                py: 0,
                                lineHeight: 1,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ 
                      color: '#999', 
                      fontSize: '0.65rem',
                      textAlign: 'center',
                    }}>
                      No hay cambios de estado registrados
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Contenedor para los 3 recuadros inferiores + nuevo recuadro vertical */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: showSideSlider ? '5fr 1fr' : '1fr',
              gap: 0.5, // Separación reducida entre las columnas
              mb: 0.85 
            }}>
              {/* Contenedor para los 3 recuadros inferiores (80% del ancho) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, position: 'relative', zIndex: 1 }}>
                {/* 3. Timer */}
                <Card
                  sx={{
                    ...CARD_SURFACE_SX,
                    p: 1.5,
                    pt: 0.65,
                    pb: 0.45, // sin hueco bajo chips
                    height: '70px', // ajustado al contenido
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: isPlus1 ? 'not-allowed' : 'pointer',
                    opacity: isPlus1 ? 0.5 : 1,
                    '&:hover': {
                      filter: isPlus1 ? 'none' : 'brightness(1.12)',
                    },
                  }}
                  onClick={(e) => {
                    if (isPlus1) return;
                    // Verificar si el usuario es demo
                    const currentUser = getUser();
                    const isDemoUser = currentUser && isDemo(currentUser);
                    if (isDemoUser) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.demoRestriction')
                      });
                      return;
                    }
                    if (!canControl) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.noControlDevices')
                      });
                      return;
                    }
                    setTimerDialogOpen(true);
                  }}
                >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <TimerIcon sx={{ color: isPlus1 ? 'rgba(255,255,255,0.25)' : ACCENT, fontSize: '1.05rem', opacity: isPlus1 ? 1 : 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: isPlus1 ? 'rgba(255,255,255,0.25)' : '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  Timer
                </Typography>
              </Box>
                <Switch
                  checked={timerEnabled}
                  disabled={isPlus1 || !canControl}
                  onChange={(e) => {
                    if (isPlus1) return;
                    // Verificar si el usuario es demo
                    const currentUser = getUser();
                    const isDemoUser = currentUser && isDemo(currentUser);
                    if (isDemoUser) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.demoRestriction')
                      });
                      return;
                    }
                    if (!canControl) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.noControlDevices')
                      });
                      return;
                    }
                    e.stopPropagation();
                    const newValue = e.target.checked;
                    if (newValue && !timerEnabled) {
                      // Solo abre diálogo si la configuración está por defecto
                      if (isTimerDefault()) {
                        setShouldOpenTimerDialog(true);
                      }
                    }
                    setTimerEnabled(newValue);
                    // Guardar automáticamente el cambio
                    console.log(`🔄 Timer switch cambiado a: ${newValue}`);
                    // Usar el valor actualizado directamente sin setTimeout
                    console.log('💾 Guardando Timer automáticamente...');
                    saveDeviceSchedulesWithValues(newValue, agendaEnabled);
                  }}
                  onClick={(e) => {
                    if (isPlus1) {
                      e.stopPropagation();
                      e.preventDefault();
                    } else {
                      e.stopPropagation();
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: timerEnabled ? '#2391FF' : 'white',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: timerEnabled ? 'rgba(35, 145, 255, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                    },
                    opacity: isPlus1 ? 0.5 : 1,
                  }}
                />
              </Box>
              <TimerSummaryChips
                isPlus1={!!isPlus1}
                timerEnabled={timerEnabled}
                isDefault={isTimerDefault()}
                autoOnEnabled={autoOnEnabled}
                autoOffEnabled={autoOffEnabled}
                oneTimeOnly={oneTimeOnly}
                autoOnHours={autoOnHours}
                autoOnMinutes={autoOnMinutes}
                autoOnSeconds={autoOnSeconds}
                autoOffHours={autoOffHours}
                autoOffMinutes={autoOffMinutes}
                autoOffSeconds={autoOffSeconds}
                configureHint={t('deviceModal.configureTimers') as string}
                labelOn={t('deviceModal.agendaActionOn') as string}
                labelOff={t('deviceModal.agendaActionOff') as string}
                labelOnceLine1={t('deviceModal.timerOnceLine1') as string}
                labelOnceLine2={t('deviceModal.timerOnceLine2') as string}
              />
                </Card>

                {/* 4. Agenda */}
                <DeviceAgendaPanel
                  device={device}
                  canControl={canControl}
                  isDemo={(() => { const u = getUser(); return Boolean(u && isDemo(u)); })()}
                  onDemoBlock={() => setPermissionSnackbar({ open: true, message: t('permissions.demoRestriction') })}
                  onPermissionBlock={() => setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') })}
                  cardSx={{ ...CARD_SURFACE_SX, '&:hover': { filter: 'brightness(1.12)' } }}
                />

                {/* 5. Botón de Power con información adicional */}
                <Card
                  sx={{
                    ...CARD_SURFACE_SX,
                    px: 1.5,
                    py: 0.9, // -~10px vs p:1.5; se suma al historial
                    background: controlGlowBackground(!!isOn),
                    transition: 'background 280ms ease',
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
              <Grid container spacing={1} alignItems="center">
                {/* PLUS2PM: Botones de persiana centrados */}
                {(localDevice || device)?.tipo === 'PLUS2PM' ? (
                  <>
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CoverControlButtons
                          deviceId={(localDevice || device).id}
                          coverAction={coverActions[(localDevice || device).id] || null}
                          isConfirming={confirmingDevices.has((localDevice || device).id)}
                          online={(localDevice || device).online}
                          authRequired={(localDevice || device).auth_required}
                          onControlCover={handleControlCover}
                          deviceState={String((localDevice || device)?.estado || 'stopped')}
                          coverPosition={(localDevice || device)?.cover_position}
                          onApertureUpdate={handleApertureUpdateFromButtons}
                        />
                      </Box>
                    </Grid>
                    {!isSettingsMode && (
                      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                          onClick={handleToggleFavorite}
                          sx={{
                            padding: 0.5,
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)'
                            }
                          }}
                        >
                          {isFavorite ? (
                            <Star
                              sx={{
                                color: '#FFD700',
                                fontSize: 28,
                                filter: 'drop-shadow(0px 0px 8px rgba(255, 215, 0, 0.8))',
                                transition: 'all 0.3s ease'
                              }}
                            />
                          ) : (
                            <StarBorder
                              sx={{
                                color: 'white',
                                fontSize: 28,
                                transition: 'all 0.3s ease'
                              }}
                            />
                          )}
                        </IconButton>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Box sx={{ pl: 1 }}>
                        {isSettingsMode ? (
                          <Typography sx={{ 
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ color: '#2391FF' }}>{(localDevice || device)?.tipo || 'N/A'}</span>
                            <span style={{ color: '#2391FF' }}> - ID: {(localDevice || device)?.id || 'N/A'}</span>
                          </Typography>
                        ) : (
                          <Typography sx={{ 
                            color: (() => {
                              const currentDevice = localDevice || device;
                              const consumo = currentDevice?.tipo === 'PLUS2PM' 
                                ? ((String(currentDevice?.estado) === 'opening' || String(currentDevice?.estado) === 'closing') 
                                    ? currentDevice?.consumo || 0 
                                    : 0)
                                : (currentDevice?.consumo || 0);
                              return consumo > 0 ? '#2391FF' : 'white';
                            })(),
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            Consumo: {formatConsumption(
                              (localDevice || device)?.tipo === 'PLUS2PM' 
                                ? ((String((localDevice || device)?.estado) === 'opening' || String((localDevice || device)?.estado) === 'closing') 
                                    ? (localDevice || device)?.consumo || 0 
                                    : 0)
                                : (localDevice || device)?.consumo || 0
                            )}
                          </Typography>
                        )}
                        {isSettingsMode ? (
                          <>
                            {(localDevice || device)?.ip && (
                              <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                                IP: {(localDevice || device).ip}
                              </Typography>
                            )}
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3, display: 'flex', alignItems: 'center' }}>
                              <span>MAC:</span>
                              <span style={{ marginLeft: '4px' }}>{(localDevice || device)?.mac || 'No disponible'}</span>
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                              {t('deviceModal.applianceType')}:
                            </Typography>
                            <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.3 }}>
                              {(localDevice || device)?.tipo_artefacto || t('deviceModal.undefined')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* NO PLUS2PM: Botón a la izquierda, info a la derecha con timer */}
                    {/* Botón de power con timer - a la izquierda */}
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1 }}>
                        <PowerButton
                          isOn={isOn}
                          isUnknown={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                          isLoading={confirmingDevices.has((localDevice || device)?.id)}
                          onClick={handleToggleDevice}
                          deviceId={(localDevice || device)?.id}
                          timerConfig={(localDevice || device)?.timer_config}
                          isTimerActive={deviceTimerStates[(localDevice || device)?.id]?.isTimerActive || false}
                          timerStartTime={deviceTimerStates[(localDevice || device)?.id]?.timerStartTime}
                          timerPosition="left"
                          isDemo={userIsDemo}
                          onDemoRestriction={() => {
                            setPermissionSnackbar({
                              open: true,
                              message: t('permissions.demoRestriction')
                            });
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Estrella de favorito */}
                    {!isSettingsMode && (
                      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <IconButton
                          onClick={handleToggleFavorite}
                          sx={{
                            padding: 0.5,
                            minWidth: 'auto',
                            mr: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)'
                            }
                          }}
                        >
                          {isFavorite ? (
                            <Star
                              sx={{
                                color: '#FFD700',
                                fontSize: 28,
                                filter: 'drop-shadow(0px 0px 8px rgba(255, 215, 0, 0.8))',
                                transition: 'all 0.3s ease'
                              }}
                            />
                          ) : (
                            <StarBorder
                              sx={{
                                color: 'white',
                                fontSize: 28,
                                transition: 'all 0.3s ease'
                              }}
                            />
                          )}
                        </IconButton>
                      </Grid>
                    )}

                    {/* Información del dispositivo - solo Consumo, IP, MAC */}
                    <Grid item xs={6}>
                      <Box sx={{ pl: 1 }}>
                        {isSettingsMode ? (
                          <Typography sx={{ 
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ color: '#2391FF' }}>{(localDevice || device)?.tipo || 'N/A'}</span>
                            <span style={{ color: '#2391FF' }}> - ID: {(localDevice || device)?.id || 'N/A'}</span>
                          </Typography>
                        ) : (
                          <Typography sx={{ 
                            color: (() => {
                              const currentDevice = localDevice || device;
                              const consumo = currentDevice?.consumo || 0;
                              return consumo > 0 ? '#2391FF' : 'white';
                            })(),
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            {t('deviceModal.consumption')}: {formatConsumption((localDevice || device)?.consumo || 0)}
                          </Typography>
                        )}
                        {isSettingsMode ? (
                          <>
                            {(localDevice || device)?.ip && (
                              <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                                IP: {(localDevice || device).ip}
                              </Typography>
                            )}
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3, display: 'flex', alignItems: 'center' }}>
                              <span>MAC:</span>
                              <span style={{ marginLeft: '4px' }}>{(localDevice || device)?.mac || 'No disponible'}</span>
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                              {t('deviceModal.applianceType')}:
                            </Typography>
                            <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.3 }}>
                              {(localDevice || device)?.tipo_artefacto || t('deviceModal.undefined')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                )}

              </Grid>
            </Card>
              </Box>

              {showSideSlider && (
              <Box sx={{ 
                width: '100%',
                height: '219px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                minHeight: 0,
              }}>
                {/* Slider de Brillo para DIMMER2 */}
                {(localDevice || device) && ((localDevice || device).tipo === 'DIMMER2' || (localDevice || device).tipo === 'dimmer2' || (localDevice || device).tipo?.toLowerCase() === 'dimmer2' || (localDevice || device).lights_brightness !== undefined) && (
                  <Card
                    sx={{
                      ...SLIDER_CARD_SX,
                      p: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px'
                    }}
                  >
                    <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.5 }}>
                      {t('deviceModal.brightness')}
                    </Typography>
                    <EditablePercent
                      value={currentBrightness}
                      disabled={!(localDevice || device)?.online || !!(localDevice || device)?.auth_required || !canControl}
                      ariaLabel={t('deviceModal.brightness')}
                      onCommit={(n) => {
                        void handleBrightnessChangeCommitted({} as React.SyntheticEvent, n);
                      }}
                    />
                    <Slider
                      orientation="vertical"
                      value={localBrightness}
                      onChange={handleBrightnessChange}
                      onChangeCommitted={handleBrightnessChangeCommitted}
                      disabled={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                      min={0}
                      max={100}
                      step={1}
                      sx={{
                        height: '140px',
                        width: '100%',
                        '& .MuiSlider-track': {
                          background: `linear-gradient(to top, #0d3a6b 0%, #1a5fb3 25%, #2391FF 50%, #4A9EFF 75%, #6BB6FF 100%)`,
                          border: 'none',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-thumb': {
                          backgroundColor: 'white',
                          border: 'none',
                          width: '30px', // Reducido de 42px a 30px
                          height: '6px',
                          borderRadius: '2px',
                          marginLeft: '0px',
                          marginBottom: '-7px', // Ajustado para que en 0% no se oculte
                          '&:hover': {
                            boxShadow: 'none'
                          },
                          '&:before': {
                            display: 'none'
                          }
                        }
                      }}
                    />
                  </Card>
                )}

                {/* Slider de Apertura para PLUS2PM */}
                {(localDevice || device) && (localDevice || device).tipo === 'PLUS2PM' && (
                  <Card
                    sx={{
                      ...SLIDER_CARD_SX,
                      p: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px'
                    }}
                  >
                    <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.5 }}>
                      Apertura
                    </Typography>
                    <EditablePercent
                      value={localAperture}
                      disabled={!(localDevice || device)?.online || !!(localDevice || device)?.auth_required || !canControl}
                      ariaLabel="Apertura"
                      onCommit={(n) => {
                        void handleApertureChangeCommitted({} as React.SyntheticEvent, n);
                      }}
                    />
                    <Slider
                      orientation="vertical"
                      value={localAperture}
                      onChange={handleApertureChange}
                      onChangeCommitted={handleApertureChangeCommitted}
                      disabled={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                      min={0}
                      max={100}
                      step={1}
                      sx={{
                        height: '140px',
                        width: '100%',
                        '& .MuiSlider-track': {
                          background: `linear-gradient(to top, #0d3a6b 0%, #1a5fb3 25%, #2391FF 50%, #4A9EFF 75%, #6BB6FF 100%)`,
                          border: 'none',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-thumb': {
                          backgroundColor: 'white',
                          border: 'none',
                          width: '30px', // Reducido de 42px a 30px
                          height: '6px',
                          borderRadius: '2px',
                          marginLeft: '0px',
                          marginBottom: '-7px', // Ajustado para que en 0% no se oculte
                          '&:hover': {
                            boxShadow: 'none'
                          },
                          '&:before': {
                            display: 'none'
                          }
                        }
                      }}
                    />
                  </Card>
                )}
              </Box>
              )}
            </Box>
          </Box>
        </Paper>
          </Box>
        </Slide>

      {/* Dialog para Timer */}
      <Dialog
        open={timerDialogOpen}
        onClose={() => setTimerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 500 }}>
          {t('deviceModal.configureTimer')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={resetTimerToDefault} 
              sx={{ 
                color: '#ff6b6b', 
                fontSize: '0.8rem',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  color: '#ff5252'
                }
              }}
              size="small"
              title={t('deviceModal.resetTimer')}
            >
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
            <IconButton onClick={() => setTimerDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Auto On Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch 
                  size="small" 
                  checked={autoOnEnabled}
                  onChange={(e) => setAutoOnEnabled(e.target.checked)}
                />
                <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                  {t('deviceModal.autoOn')}
          </Typography>
              </Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.75rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.ifOffTurnOnAfter')} <span style={{ color: '#2391FF' }}>01 min</span>
              </Typography>
              <TimePickerComponent
                hours={autoOnHours}
                minutes={autoOnMinutes}
                seconds={autoOnSeconds}
                onHoursChange={handleAutoOnHoursChange}
                onMinutesChange={handleAutoOnMinutesChange}
                onSecondsChange={handleAutoOnSecondsChange}
                label={t('deviceModal.autoOn')}
              />
              <Typography sx={{ color: '#666', fontSize: '0.7rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.valueRange')}
              </Typography>
            </Box>

            {/* Auto Off Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch 
                  size="small" 
                  checked={autoOffEnabled}
                  onChange={(e) => setAutoOffEnabled(e.target.checked)}
                />
                <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                  {t('deviceModal.autoOff')}
                </Typography>
              </Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.75rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.ifOnTurnOffAfter')} <span style={{ color: '#2391FF' }}>01 min</span>
              </Typography>
              <TimePickerComponent
                hours={autoOffHours}
                minutes={autoOffMinutes}
                seconds={autoOffSeconds}
                onHoursChange={handleAutoOffHoursChange}
                onMinutesChange={handleAutoOffMinutesChange}
                onSecondsChange={handleAutoOffSecondsChange}
                label={t('deviceModal.autoOff')}
              />
              <Typography sx={{ color: '#666', fontSize: '0.7rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.valueRange')}
              </Typography>
            </Box>
          </Box>
          
          {/* Opción Una sola vez */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #444' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Switch 
                size="small" 
                checked={oneTimeOnly}
                onChange={(e) => setOneTimeOnly(e.target.checked)}
              />
              <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                {t('deviceModal.oneTimeOnlyDescription')}
              </Typography>
            </Box>
          </Box>
          
          {/* Botón Guardar único */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="medium"
              onClick={async () => {
                await saveDeviceSchedules();
                setTimerDialogOpen(false);
              }}
              sx={{
                backgroundColor: '#2391FF',
                '&:hover': { backgroundColor: '#1a7ae6' },
                fontSize: '0.9rem',
                py: 1,
                px: 2.5,
                minWidth: '120px'
              }}
            >
              {t('deviceModal.saveButton')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog para Agenda */}
      <Dialog
        open={agendaDialogOpen}
        onClose={handleAgendaClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 500 }}>
          {t('deviceModal.createSchedule')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={resetAgendaToDefault} 
              sx={{ 
                color: '#ff6b6b', 
                fontSize: '0.8rem',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  color: '#ff5252'
                }
              }}
              size="small"
              title={t('deviceModal.resetAgenda')}
            >
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
            <IconButton onClick={handleAgendaClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        {/* Navegación de pasos */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #444', px: 2 }}>
          {[t('deviceModal.stepDays'), t('deviceModal.stepTime'), t('deviceModal.stepAction'), t('deviceModal.stepSummary')].map((step, index) => (
            <Box
              key={index}
              onClick={() => setAgendaStep(index + 1)}
              sx={{
                flex: 1,
                py: 2,
                textAlign: 'center',
                borderBottom: agendaStep === index + 1 ? '2px solid #2391FF' : '2px solid transparent',
                color: agendaStep === index + 1 ? '#2391FF' : '#aaa',
                cursor: 'pointer',
                '&:hover': {
                  color: '#2391FF',
                  backgroundColor: 'rgba(35, 145, 255, 0.1)'
                }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {step}
          </Typography>
            </Box>
          ))}
        </Box>

        <DialogContent sx={{ pt: 3, minHeight: '400px' }}>
          {/* Paso 1: Días */}
          {agendaStep === 1 && (
            <Box>
              
              {/* Días de la semana - más compacto */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'space-between' }}>
                {[t('deviceModal.weekDaysShort.mon'), t('deviceModal.weekDaysShort.tue'), t('deviceModal.weekDaysShort.wed'), t('deviceModal.weekDaysShort.thu'), t('deviceModal.weekDaysShort.fri'), t('deviceModal.weekDaysShort.sat'), t('deviceModal.weekDaysShort.sun')].map((day, index) => (
                  <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'white', mb: 0.5, fontSize: '0.7rem' }}>
                      {day}
                    </Typography>
                    <Switch
                      size="small"
                      checked={selectedDays.includes(index + 1)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDays([...selectedDays, index + 1]);
                        } else {
                          setSelectedDays(selectedDays.filter(d => d !== index + 1));
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#2391FF',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#2391FF',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Seleccionar días específicos del mes */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Switch
                    size="small"
                    checked={specificDaysEnabled}
                    onChange={(e) => setSpecificDaysEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2391FF',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2391FF',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                    {t('deviceModal.selectSpecificDaysOfMonth')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.7rem' }}>
                  {t('deviceModal.appliesToAllDays')}
                </Typography>
                
                {specificDaysEnabled && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Button
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const evenDays = Array.from({ length: 31 }, (_, i) => i + 1).filter(d => d % 2 === 0);
                          setSelectedDaysOfMonth(Array.from(new Set([...selectedDaysOfMonth, ...evenDays])));
                        }}
                      >
                        {t('deviceModal.even')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const oddDays = Array.from({ length: 31 }, (_, i) => i + 1).filter(d => d % 2 === 1);
                          setSelectedDaysOfMonth(Array.from(new Set([...selectedDaysOfMonth, ...oddDays])));
                        }}
                      >
                        {t('deviceModal.odd')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => setSelectedDaysOfMonth([])}
                      >
                        {t('deviceModal.clear')}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <Button
                          key={day}
                          size="small"
                          variant={selectedDaysOfMonth.includes(day) ? 'contained' : 'outlined'}
                          sx={{
                            minWidth: '20px',
                            height: '20px',
                            fontSize: '0.6rem',
                            backgroundColor: selectedDaysOfMonth.includes(day) ? '#2391FF' : 'transparent',
                            color: selectedDaysOfMonth.includes(day) ? 'white' : '#aaa',
                            borderColor: SURFACE_BORDER,
                            '&:hover': {
                              backgroundColor: selectedDaysOfMonth.includes(day) ? '#1a7ae6' : '#333',
                            }
                          }}
                          onClick={() => {
                            if (selectedDaysOfMonth.includes(day)) {
                              setSelectedDaysOfMonth(selectedDaysOfMonth.filter(d => d !== day));
                            } else {
                              setSelectedDaysOfMonth([...selectedDaysOfMonth, day]);
                            }
                          }}
                        >
                          {day}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Seleccionar meses específicos */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Switch
                    size="small"
                    checked={specificMonthsEnabled}
                    onChange={(e) => setSpecificMonthsEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2391FF',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2391FF',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                    {t('deviceModal.selectSpecificMonths')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.7rem' }}>
                  {t('deviceModal.appliesToAllMonths')}
                </Typography>
                
                {specificMonthsEnabled && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const evenMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => m % 2 === 0);
                          setSelectedMonths(Array.from(new Set([...selectedMonths, ...evenMonths])));
                        }}
                      >
                        {t('deviceModal.even')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const oddMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => m % 2 === 1);
                          setSelectedMonths(Array.from(new Set([...selectedMonths, ...oddMonths])));
                        }}
                      >
                        {t('deviceModal.odd')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => setSelectedMonths([])}
                      >
                        {t('deviceModal.clear')}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                      {[t('deviceModal.months.jan'), t('deviceModal.months.feb'), t('deviceModal.months.mar'), t('deviceModal.months.apr'), t('deviceModal.months.may'), t('deviceModal.months.jun'), t('deviceModal.months.jul'), t('deviceModal.months.aug'), t('deviceModal.months.sep'), t('deviceModal.months.oct'), t('deviceModal.months.nov'), t('deviceModal.months.dec')].map((month, index) => (
                        <Button
                          key={month}
                          size="small"
                          variant={selectedMonths.includes(index + 1) ? 'contained' : 'outlined'}
                          sx={{
                            minWidth: '40px',
                            height: '25px',
                            fontSize: '0.6rem',
                            backgroundColor: selectedMonths.includes(index + 1) ? '#2391FF' : 'transparent',
                            color: selectedMonths.includes(index + 1) ? 'white' : '#aaa',
                            borderColor: SURFACE_BORDER,
                            '&:hover': {
                              backgroundColor: selectedMonths.includes(index + 1) ? '#1a7ae6' : '#333',
                            }
                          }}
                          onClick={() => {
                            if (selectedMonths.includes(index + 1)) {
                              setSelectedMonths(selectedMonths.filter(m => m !== index + 1));
                            } else {
                              setSelectedMonths([...selectedMonths, index + 1]);
                            }
                          }}
                        >
                          {month}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Paso 2: Tiempo */}
          {agendaStep === 2 && (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Typography variant="body2" sx={{ color: 'white', mb: 2, fontSize: '0.8rem' }}>
                {t('deviceModal.selectSchedules')}
              </Typography>
              
              {scheduleTimes.map((time, index) => (
                <Box key={index} sx={{ position: 'relative', mb: 0.05 }}>
                  {/* Botón X para eliminar (solo si no es el primero) */}
                  {index > 0 && (
                    <IconButton
                      onClick={() => removeScheduleTime(index)}
                      sx={{ 
                        position: 'absolute',
                        top: 4,
                        right: 8,
                        zIndex: 1,
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#ff4444',
                        '&:hover': {
                          backgroundColor: '#cc3333'
                        }
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>×</Typography>
                    </IconButton>
                  )}
                  
                  <TimePickerComponent
                    hours={time.hours}
                    minutes={time.minutes}
                    seconds={time.seconds}
                    onHoursChange={(value) => {
                      if (value > 23) {
                        setTimeError(t('deviceModal.hoursError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'hours', value);
                    }}
                    onMinutesChange={(value) => {
                      if (value > 59) {
                        setTimeError(t('deviceModal.minutesError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'minutes', value);
                    }}
                    onSecondsChange={(value) => {
                      if (value > 59) {
                        setTimeError(t('deviceModal.secondsError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'seconds', value);
                    }}
                    label={`${t('deviceModal.schedule')} ${index + 1}`}
                  />
                </Box>
              ))}
              
              {/* Botón "Agregar Horario" */}
              {scheduleTimes.length < 48 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    onClick={addScheduleTime}
                    variant="outlined"
                    fullWidth
                    sx={{
                      color: '#2391FF',
                      borderColor: '#2391FF',
                      '&:hover': {
                        borderColor: '#1a7ae6',
                        backgroundColor: 'rgba(35, 145, 255, 0.1)'
                      }
                    }}
                  >
                    {t('deviceModal.addSchedule')}
                  </Button>
                </Box>
              )}
              
              {timeError && (
                <Typography variant="body2" sx={{ color: '#ff4444', mt: 1, fontSize: '0.8rem' }}>
                  {timeError}
                </Typography>
              )}
              
              <Typography variant="body2" sx={{ color: '#aaa', mt: 2, fontSize: '0.7rem' }}>
                {scheduleTimes.length} {t('deviceModal.schedulesConfigured')}
              </Typography>
            </Box>
          )}

          {/* Paso 3: Acción */}
          {agendaStep === 3 && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 1, fontSize: '0.8rem' }}>
                  {t('deviceModal.selectAction')}
                </Typography>
                <RadioGroup
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}
                >
                  {['On', 'Off', 'Cambiar Estado'].map((action) => (
                    <FormControlLabel
                      key={action}
                      value={action}
                      control={
                        <Radio
                          sx={{
                            color: '#2391FF',
                            '&.Mui-checked': {
                              color: '#2391FF',
                            },
                          }}
                        />
                      }
                      label={action === 'On' ? t('deviceModal.turnOn') : action === 'Off' ? t('deviceModal.turnOff') : t('deviceModal.changeState')}
                      sx={{ color: 'white', fontSize: '0.8rem' }}
                    />
                  ))}
                </RadioGroup>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={toggleEnabled}
                      onChange={(e) => setToggleEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#2391FF',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#2391FF',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                      {t('deviceModal.toggleAfterTime')}
                    </Typography>
                  }
                />
                <Typography variant="body2" sx={{ color: '#aaa', mt: 1, fontSize: '0.7rem' }}>
                  {t('deviceModal.returnsToPreviousState')}
                </Typography>
                
                {toggleEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <TimePickerComponent
                      hours={Math.floor(toggleAfterSeconds / 3600)}
                      minutes={Math.floor((toggleAfterSeconds % 3600) / 60)}
                      seconds={toggleAfterSeconds % 60}
                      onHoursChange={(value) => {
                        const minutes = Math.floor((toggleAfterSeconds % 3600) / 60);
                        const seconds = toggleAfterSeconds % 60;
                        const maxHours = 11424; // 476 días en horas
                        const newTotalSeconds = value * 3600 + minutes * 60 + seconds;
                        
                        if (newTotalSeconds > maxHours * 3600) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      onMinutesChange={(value) => {
                        // Transformación automática: si minutos >= 60, convertir a horas
                        let hours = Math.floor(toggleAfterSeconds / 3600);
                        let minutes = value;
                        let seconds = toggleAfterSeconds % 60;
                        
                        // Aplicar transformación automática inmediatamente
                        if (minutes >= 60) {
                          hours += Math.floor(minutes / 60);
                          minutes = minutes % 60;
                        }
                        
                        const newTotalSeconds = hours * 3600 + minutes * 60 + seconds;
                        const maxSeconds = 11424 * 3600; // 476 días en segundos
                        
                        if (newTotalSeconds > maxSeconds) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      onSecondsChange={(value) => {
                        // Transformación automática: si segundos >= 60, convertir a minutos
                        let hours = Math.floor(toggleAfterSeconds / 3600);
                        let minutes = Math.floor((toggleAfterSeconds % 3600) / 60);
                        let seconds = value;
                        
                        // Aplicar transformación automática inmediatamente
                        if (seconds >= 60) {
                          minutes += Math.floor(seconds / 60);
                          seconds = seconds % 60;
                          
                          // Si minutos >= 60, convertir a horas
                          if (minutes >= 60) {
                            hours += Math.floor(minutes / 60);
                            minutes = minutes % 60;
                          }
                        }
                        
                        const newTotalSeconds = hours * 3600 + minutes * 60 + seconds;
                        const maxSeconds = 11424 * 3600; // 476 días en segundos
                        
                        if (newTotalSeconds > maxSeconds) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      label={t('deviceModal.alternationTime')}
                    />
                    {durationError && (
                      <Typography variant="body2" sx={{ color: '#ff4444', mt: 1, fontSize: '0.8rem' }}>
                        {durationError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Paso 4: Resumen */}
          {agendaStep === 4 && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.days')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    {t('deviceModal.executeSchedule')} <span style={{ color: '#2391FF' }}>
                      {selectedDays.length === 7 ? t('deviceModal.allDays') : 
                       selectedDays.length === 0 ? t('deviceModal.noDays') :
                       selectedDays.map(d => [t('deviceModal.weekDays.mon'), t('deviceModal.weekDays.tue'), t('deviceModal.weekDays.wed'), t('deviceModal.weekDays.thu'), t('deviceModal.weekDays.fri'), t('deviceModal.weekDays.sat'), t('deviceModal.weekDays.sun')][d-1]).join(', ')}
                    </span>
                    {specificDaysEnabled && selectedDaysOfMonth.length > 0 && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.days')} {selectedDaysOfMonth.join(', ')})</span>
                    )}
                    {specificMonthsEnabled && selectedMonths.length > 0 && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.month')} {selectedMonths.map(m => [t('deviceModal.months.jan'), t('deviceModal.months.feb'), t('deviceModal.months.mar'), t('deviceModal.months.apr'), t('deviceModal.months.may'), t('deviceModal.months.jun'), t('deviceModal.months.jul'), t('deviceModal.months.aug'), t('deviceModal.months.sep'), t('deviceModal.months.oct'), t('deviceModal.months.nov'), t('deviceModal.months.dec')][m-1]).join(', ')})</span>
                    )}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.schedules')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    {t('deviceModal.executeScheduleAt')} <span style={{ color: '#2391FF' }}>
                      {scheduleTimes.map(time => 
                        `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:${time.seconds.toString().padStart(2, '0')}`
                      ).join(', ')}
                    </span>
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.action')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    <span style={{ color: '#2391FF' }}>
                      {actionType === 'On' ? t('deviceModal.turnOn') : actionType === 'Off' ? t('deviceModal.turnOff') : actionType === 'Cambiar Estado' ? t('deviceModal.changeState') : t('deviceModal.toggle')}
                    </span>
                    {toggleEnabled && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.changesAfter')} {Math.floor(toggleAfterSeconds / 3600)}h {Math.floor((toggleAfterSeconds % 3600) / 60)}m {toggleAfterSeconds % 60}s)</span>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #444', p: 2 }}>
          {agendaStep > 1 && (
          <Button
              onClick={handleAgendaBack}
              sx={{
                color: 'white',
                borderColor: SURFACE_BORDER,
                '&:hover': {
                  borderColor: '#2391FF',
                  backgroundColor: '#333'
                }
              }}
              variant="outlined"
            >
              {t('deviceModal.back')}
          </Button>
          )}
          
          <Box sx={{ flex: 1 }} />
          
          {agendaStep < 4 ? (
            <Button
              onClick={handleAgendaNext}
              sx={{
                backgroundColor: '#2391FF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1a7ae6'
                }
              }}
            >
              {t('deviceModal.next')}
            </Button>
          ) : (
            <Button
              onClick={handleAgendaSave}
              sx={{
                backgroundColor: '#2391FF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1a7ae6'
                }
              }}
            >
              {t('deviceModal.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de fecha personalizada (igual que HistoricalReports) */}
      <Dialog
        open={customDateDialogOpen}
        onClose={() => setCustomDateDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: 'rgba(18,18,20,0.98)', color: 'white', borderRadius: '16px', minWidth: '300px', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: '1rem', fontWeight: 700, borderBottom: `1px solid ${SURFACE_BORDER}`, paddingBottom: 1 }}>
          {t('deviceModal.customPeriod')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            label={t('deviceModal.from')}
            type="datetime-local"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputLabel-root': { color: '#999' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER } }}
          />
          <TextField
            label={t('deviceModal.to')}
            type="datetime-local"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputLabel-root': { color: '#999' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER } }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px', borderTop: '1px solid #444' }}>
          <Button onClick={() => setCustomDateDialogOpen(false)} sx={{ color: '#999', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>{t('deviceModal.cancel')}</Button>
          <Button onClick={handleCustomDateConfirm} sx={{ color: '#2391FF', fontWeight: 'bold', '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' } }}>{t('deviceModal.confirm')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes de permisos */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert
          onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    );
  }

  // En vertical, renderizar como modal completo
  return (
    <Box
      data-testid="device-modal"
      onTouchStart={handleTouchStart}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1360,
        display: open ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'transparent',
        }}
      />
      <Transition in={!isClosing}>
        <Paper
          sx={{
            position: 'fixed',
            bottom: SHC_BOTTOM_NAV_CLEARANCE,
            top: '40px',
            left: '1%',
            right: '1%',
            width: '98%',
            backgroundColor: '#000',
            color: 'white',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '16px 16px 0 0',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            border: '1px solid #444',
            borderTop: '2px solid #2391FF',
          }}
        >
          {/* Header con nombre del dispositivo y botón cerrar - reducido 15% altura y letra */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              py: 0.55,
              borderBottom: `1px solid ${SURFACE_BORDER}`,
              background: 'linear-gradient(180deg, #1c1c1c 0%, #141414 100%)',
              minHeight: 36,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '0.82rem',
                textTransform: 'capitalize',
                textAlign: 'center',
                flex: 1,
                lineHeight: 1.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 0.5,
              }}
            >
              {(localDevice || device)?.nombre}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                padding: '4px',
                flexShrink: 0,
                transition: PRESS_TRANSITION,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
                '&:active': { transform: 'scale(0.9)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Contenido principal */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: 1.5,
              pt: 0.85, // Espacio entre título y primer recuadro = 0.85
              background: 'radial-gradient(120% 60% at 50% -10%, rgba(35,145,255,0.08) 0%, transparent 50%)',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'black',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: ACCENT,
                borderRadius: '3px',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#2391FF black',
            }}
          >
            {/* 1. Gráfico de consumo */}
            <Card
              sx={{
                ...CHART_CARD_SX,
                mb: 0.85, // Espacio entre recuadros = 0.85
                p: 1.5,
                pt: 0.75, // Reducido padding superior a la mitad
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <TimelineIcon sx={{ color: ACCENT, mr: 1, fontSize: '1.05rem', opacity: 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={SECTION_TITLE_SX}
                >
                  {t('deviceModal.consumptionGraph')}
                </Typography>
              </Box>
              
              {/* Separación entre título y filtros */}
              <Box sx={{ height: '2px', mb: 0 }} />
              
              {/* Filtros - Primer renglón (igual que HistoricalReports) */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'nowrap', mb: 1.5, mt: 0.25, pr: 0.25, minWidth: 0 }}>
                <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                  <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.period')}</InputLabel>
                  <Select
                    value={period}
                    onChange={(e) => handlePeriodChange(e.target.value as PeriodOption)}
                    label={t('deviceModal.period')}
                    sx={{ 
                      color: '#fff', 
                      fontSize: 12,
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' }, 
                      '& .MuiSvgIcon-root': { color: MUTED, fontSize: 18 }
                    }}
                    MenuProps={{
                      style: CHART_MENU_LAYER_STYLE,
                      PaperProps: {
                        sx: MENU_PAPER_SX
                      }
                    }}
                  >
                    <MenuItem value="1hour">{t('reports.periods.1hour')}</MenuItem>
                    <MenuItem value="6hours">{t('reports.periods.6hours')}</MenuItem>
                    <MenuItem value="12hours">{t('reports.periods.12hours')}</MenuItem>
                    <MenuItem value="24hours">{t('reports.periods.24hours')}</MenuItem>
                    <MenuItem value="7days">{t('reports.periods.7days')}</MenuItem>
                    <MenuItem value="30days">{t('reports.periods.30days')}</MenuItem>
                    <MenuItem value="12months">{t('reports.periods.12months')}</MenuItem>
                    <MenuItem value="today">{t('reports.periods.today')}</MenuItem>
                    <MenuItem value="yesterday">{t('reports.periods.yesterday')}</MenuItem>
                    <MenuItem value="thisWeek">{t('reports.periods.thisWeek')}</MenuItem>
                    <MenuItem value="thisMonth">{t('reports.periods.thisMonth')}</MenuItem>
                    <MenuItem value="thisYear">{t('reports.periods.thisYear')}</MenuItem>
                    <MenuItem value="billing">{t('reports.periods.billing')}</MenuItem>
                    <MenuItem value="custom">{t('reports.periods.custom')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
                  <InputLabel sx={{ color: MUTED, fontSize: 11 }}>{t('deviceModal.interval')}</InputLabel>
                  <Select
                    value={interval}
                    onChange={(e) => {
                      const newInterval = e.target.value as IntervalOption;
                      // Tracking para usuarios Demo
                      const currentUser = getUser();
                      if (currentUser && isDemo(currentUser)) {
                        const tracker = getDemoActivityTracker();
                        tracker.trackFilterChange('interval', newInterval, {
                          screen: 'device_modal',
                          deviceId: device?.id || null
                        });
                      }
                      setChartInterval(newInterval);
                    }}
                    label={t('deviceModal.interval')}
                    sx={{ 
                      color: '#fff', 
                      fontSize: 12,
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' }, 
                      '& .MuiSvgIcon-root': { color: MUTED, fontSize: 18 }
                    }}
                    MenuProps={{
                      style: CHART_MENU_LAYER_STYLE,
                      PaperProps: {
                        sx: MENU_PAPER_SX
                      }
                    }}
                  >
                    {/* ✅ REFACTORIZACIÓN: Mostrar solo intervalos válidos según período (igual que HistoricalReports) */}
                    {getAvailableIntervals().map(intv => (
                      <MenuItem key={intv} value={intv}>{t(`reports.intervals.${intv}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeviceModalNavigateDay('previous')}
                      aria-label={t('solarEnergy.tab2.dateSelector.previousDay')}
                      sx={{
                        color: '#999',
                        padding: '2px',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <ChevronLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleDeviceModalGoToToday}
                      disabled={period === 'today' && !customStartDate}
                      aria-label={t('solarEnergy.tab2.dateSelector.today')}
                      title={t('solarEnergy.tab2.dateSelector.today')}
                      sx={{
                        color: '#999',
                        padding: '2px',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                        '&.Mui-disabled': { color: '#444' },
                      }}
                    >
                      <TodayIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeviceModalNavigateDay('next')}
                      disabled={!deviceModalCanNavigateNextDay}
                      aria-label={t('solarEnergy.tab2.dateSelector.nextDay')}
                      sx={{
                        color: deviceModalCanNavigateNextDay ? '#999' : '#444',
                        padding: '2px',
                        '&:hover': deviceModalCanNavigateNextDay
                          ? { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
                          : {},
                        '&.Mui-disabled': { color: '#444' },
                      }}
                    >
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  {/* ✅ REFACTORIZACIÓN: Eliminado selector potencia/energía - siempre mostramos potencia en gráfico y energía en leyendas */}
                  <IconButton
                    onClick={() => {
                      setDeviceChartData([]);
                      setDeviceChartDataEnergy([]);
                      loadDeviceChartData();
                    }}
                    sx={{
                      color: '#999',
                      padding: '2px',
                      marginLeft: '-2px',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: '#2391FF',
                      },
                    }}
                    size="small"
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Gráfico de consumo del dispositivo */}
              {loadingChart ? (
                <Box sx={{ height: '145px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} sx={{ color: ACCENT }} />
                </Box>
              ) : formatDeviceChartData.length > 0 ? (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{
                    height: '145px',
                    width: '100%',
                    borderRadius: '14px',
                    bgcolor: 'rgba(0,0,0,0.22)',
                    border: `1px solid ${SURFACE_BORDER}`,
                    px: 0.5,
                    pt: 0.5,
                    pb: 0.5,
                    overflow: 'hidden',
                  }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={formatDeviceChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="4 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis 
                          dataKey="time" 
                          stroke="rgba(255,255,255,0.35)" 
                          tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 9 }} 
                          angle={-45} 
                          textAnchor="end" 
                          height={50}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          stroke="rgba(255,255,255,0.35)" 
                          tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 9 }} 
                          label={{ 
                            value: t('deviceModal.consumption'), // Siempre mostrar consumo (potencia)
                            angle: -90, 
                            position: 'insideLeft', 
                            style: { fill: 'rgba(255,255,255,0.45)', fontSize: 9 } 
                          }}
                          tickFormatter={(value) => {
                            // ✅ REFACTORIZACIÓN: Siempre mostrar potencia en eje Y
                            const absValue = Math.abs(value);
                            if (absValue >= 1000) {
                              return `${(value / 1000).toFixed(1)} kW`;
                            }
                            return `${Math.round(value)} W`;
                          }}
                          width={50}
                        />
                        <Tooltip 
                          content={<CustomTooltip />}
                          allowEscapeViewBox={{ x: false, y: true }}
                          position={{ y: 90 }}
                          offset={-5}
                          wrapperStyle={{ 
                            zIndex: 1000
                          }}
                        />
                        {/* ✅ REFACTORIZACIÓN: Usar Area para mostrar energía debajo de la curva (igual que HistoricalReports) */}
                        <Area
                          type="monotone"
                          dataKey="consumo"
                          stroke="none"
                          fill={ACCENT}
                          fillOpacity={0.22}
                          strokeWidth={0}
                          dot={false}
                          connectNulls={false}
                          baseValue={0}
                          name={device?.nombre || t('deviceModal.consumption')}
                        />
                        {/* ✅ REFACTORIZACIÓN: Usar Line para potencia encima del área (igual que HistoricalReports) */}
                        <Line 
                          type="monotone" 
                          dataKey="consumo" 
                          stroke={ACCENT} 
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls={false}
                          name={device?.nombre || t('deviceModal.consumption')}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  {/* Valor total del período */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: -0.5,
                    mb: 0,
                    height: '20px'
                  }}>
                    <Typography 
                      sx={{ 
                        color: '#00C853', // ✅ REFACTORIZACIÓN: Verde para energía en leyenda
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        lineHeight: 1
                      }}
                    >
                      {t('reports.energy')}: {(() => {
                        // ✅ REFACTORIZACIÓN: Mostrar energía total desde deviceEnergyTotal
                        const energia = deviceEnergyTotal;
                        return Math.abs(energia) >= 1000 
                          ? `${(energia / 1000).toFixed(2)} kWh` 
                          : `${energia.toFixed(2)} Wh`;
                      })()}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '145px',
                    backgroundColor: '#000',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #444',
                  }}
                >
                  <Typography sx={{ color: '#999', fontSize: 12, textAlign: 'center' }}>
                    No hay datos disponibles para el período seleccionado
                  </Typography>
                </Box>
              )}
            </Card>

            {/* 2. Historial de actividades */}
            <Card
              sx={{
                ...CARD_SURFACE_SX,
                mb: 0.85, // Espacio entre recuadros = 0.85
                p: 1.221759375, // Reducido 5% más de 1.2860625 a 1.221759375
                pt: 0.6108796875, // Reducido 5% más de 0.64303125 a 0.6108796875
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}> {/* Reducido significativamente */}
                <HistoryIcon sx={{ color: ACCENT, mr: 1, fontSize: '1.05rem', opacity: 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={SECTION_TITLE_SX}
                >
                  {t('deviceModal.activityHistory')}
                </Typography>
              </Box>

              <Box sx={{ 
                height: '91px', // +18px desde Timer+Agenda (sin hueco bajo chips)
                overflowY: 'auto',
                // Scrollbar fino y transparente
                '&::-webkit-scrollbar': {
                  width: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#333',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#2391FF',
                  borderRadius: '2px',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#2391FF #333',
              }}>
                {loadingActivityHistory ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                    <CircularProgress size={16} sx={{ color: ACCENT }} />
                  </Box>
                ) : activityHistory.length > 0 ? (
                  activityHistory.map((state, index) => {
                    // ✅ Usar fecha y hora ya formateadas desde el backend (GMT-3)
                    // El backend envía 'fecha' y 'hora' ya formateadas en GMT-3
                    const fecha = state.fecha || (() => {
                      const timestamp = new Date(state.timestamp);
                      return formatInServerTz(timestamp, 'yyyy-MM-dd');
                    })();
                    const hora = state.hora || (() => {
                      const timestamp = new Date(state.timestamp);
                      return formatInServerTz(timestamp, 'HH:mm');
                    })();
                    const estado = state.estado === true || state.estado === 'on' || state.estado === 'ON' || state.estado === 'true';
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0,
                          height: 17,
                          minHeight: 17,
                          maxHeight: 17,
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Columna 1: Fecha con indentación */}
                        <Box sx={{ flex: '0 0 auto', ml: 2.5, mr: 6, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '0.6rem',
                            lineHeight: 1,
                          }}>
                            {formatDate(fecha)}
                          </Typography>
                        </Box>
                        
                        {/* Columna 2: Hora */}
                        <Box sx={{ flex: '0 0 auto', mr: 12, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '0.6rem',
                            lineHeight: 1,
                          }}>
                            {hora} hs.
                          </Typography>
                        </Box>
                        
                        {/* Columna 3: Chip ON/OFF */}
                        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={estado ? t('deviceModal.on') : t('deviceModal.off')}
                            size="small"
                            sx={{
                              backgroundColor: estado ? '#00ff00' : '#ff4444',
                              color: 'black',
                              fontWeight: 'bold',
                              fontSize: '0.38rem',
                              height: '11px',
                              width: '28px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              '& .MuiChip-label': {
                                px: 0.4,
                                py: 0,
                                lineHeight: 1,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ 
                      color: '#999', 
                      fontSize: '0.65rem',
                      textAlign: 'center',
                    }}>
                      No hay cambios de estado registrados
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Contenedor para los 3 recuadros inferiores + nuevo recuadro vertical */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: showSideSlider ? '5fr 1fr' : '1fr',
              gap: 0.5, // Separación reducida entre las columnas
              mb: 0.85 
            }}>
              {/* Contenedor para los 3 recuadros inferiores (80% del ancho) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, position: 'relative', zIndex: 1 }}>
                {/* 3. Timer */}
                <Card
                  sx={{
                    ...CARD_SURFACE_SX,
                    p: 1.5,
                    pt: 0.65,
                    pb: 0.45, // sin hueco bajo chips
                    height: '70px', // ajustado al contenido
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: isPlus1 ? 'not-allowed' : 'pointer',
                    opacity: isPlus1 ? 0.5 : 1,
                    '&:hover': {
                      filter: isPlus1 ? 'none' : 'brightness(1.12)',
                    },
                  }}
                  onClick={(e) => {
                    if (isPlus1) return;
                    // Verificar si el usuario es demo
                    const currentUser = getUser();
                    const isDemoUser = currentUser && isDemo(currentUser);
                    if (isDemoUser) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.demoRestriction')
                      });
                      return;
                    }
                    if (!canControl) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.noControlDevices')
                      });
                      return;
                    }
                    setTimerDialogOpen(true);
                  }}
                >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <TimerIcon sx={{ color: isPlus1 ? 'rgba(255,255,255,0.25)' : ACCENT, fontSize: '1.05rem', opacity: isPlus1 ? 1 : 0.9 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: isPlus1 ? 'rgba(255,255,255,0.25)' : '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  Timer
                </Typography>
              </Box>
                <Switch
                  checked={timerEnabled}
                  disabled={isPlus1 || !canControl}
                  onChange={(e) => {
                    if (isPlus1) return;
                    // Verificar si el usuario es demo
                    const currentUser = getUser();
                    const isDemoUser = currentUser && isDemo(currentUser);
                    if (isDemoUser) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.demoRestriction')
                      });
                      return;
                    }
                    if (!canControl) {
                      e.stopPropagation();
                      setPermissionSnackbar({
                        open: true,
                        message: t('permissions.noControlDevices')
                      });
                      return;
                    }
                    e.stopPropagation();
                    const newValue = e.target.checked;
                    if (newValue && !timerEnabled) {
                      // Solo abre diálogo si la configuración está por defecto
                      if (isTimerDefault()) {
                        setShouldOpenTimerDialog(true);
                      }
                    }
                    setTimerEnabled(newValue);
                    // Guardar automáticamente el cambio
                    console.log(`🔄 Timer switch cambiado a: ${newValue}`);
                    // Usar el valor actualizado directamente sin setTimeout
                    console.log('💾 Guardando Timer automáticamente...');
                    saveDeviceSchedulesWithValues(newValue, agendaEnabled);
                  }}
                  onClick={(e) => {
                    if (isPlus1) {
                      e.stopPropagation();
                      e.preventDefault();
                    } else {
                      e.stopPropagation();
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: timerEnabled ? '#2391FF' : 'white',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: timerEnabled ? 'rgba(35, 145, 255, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                    },
                    opacity: isPlus1 ? 0.5 : 1,
                  }}
                />
              </Box>
              <TimerSummaryChips
                isPlus1={!!isPlus1}
                timerEnabled={timerEnabled}
                isDefault={isTimerDefault()}
                autoOnEnabled={autoOnEnabled}
                autoOffEnabled={autoOffEnabled}
                oneTimeOnly={oneTimeOnly}
                autoOnHours={autoOnHours}
                autoOnMinutes={autoOnMinutes}
                autoOnSeconds={autoOnSeconds}
                autoOffHours={autoOffHours}
                autoOffMinutes={autoOffMinutes}
                autoOffSeconds={autoOffSeconds}
                configureHint={t('deviceModal.configureTimers') as string}
                labelOn={t('deviceModal.agendaActionOn') as string}
                labelOff={t('deviceModal.agendaActionOff') as string}
                labelOnceLine1={t('deviceModal.timerOnceLine1') as string}
                labelOnceLine2={t('deviceModal.timerOnceLine2') as string}
              />
                </Card>

                {/* 4. Agenda */}
                <DeviceAgendaPanel
                  device={device}
                  canControl={canControl}
                  isDemo={(() => { const u = getUser(); return Boolean(u && isDemo(u)); })()}
                  onDemoBlock={() => setPermissionSnackbar({ open: true, message: t('permissions.demoRestriction') })}
                  onPermissionBlock={() => setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') })}
                  cardSx={{ ...CARD_SURFACE_SX, '&:hover': { filter: 'brightness(1.12)' } }}
                />

                {/* 5. Botón de Power con información adicional */}
                <Card
                  sx={{
                    ...CARD_SURFACE_SX,
                    px: 1.5,
                    py: 0.9, // -~10px vs p:1.5; se suma al historial
                    background: controlGlowBackground(!!isOn),
                    transition: 'background 280ms ease',
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
              <Grid container spacing={1} alignItems="center">
                {/* PLUS2PM: Botones de persiana centrados */}
                {(localDevice || device)?.tipo === 'PLUS2PM' ? (
                  <>
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CoverControlButtons
                          deviceId={(localDevice || device).id}
                          coverAction={coverActions[(localDevice || device).id] || null}
                          isConfirming={confirmingDevices.has((localDevice || device).id)}
                          online={(localDevice || device).online}
                          authRequired={(localDevice || device).auth_required}
                          onControlCover={handleControlCover}
                          deviceState={String((localDevice || device)?.estado || 'stopped')}
                          coverPosition={(localDevice || device)?.cover_position}
                          onApertureUpdate={handleApertureUpdateFromButtons}
                        />
                      </Box>
                    </Grid>
                    {!isSettingsMode && (
                      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                          onClick={handleToggleFavorite}
                          sx={{
                            padding: 0.5,
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)'
                            }
                          }}
                        >
                          {isFavorite ? (
                            <Star
                              sx={{
                                color: '#FFD700',
                                fontSize: 28,
                                filter: 'drop-shadow(0px 0px 8px rgba(255, 215, 0, 0.8))',
                                transition: 'all 0.3s ease'
                              }}
                            />
                          ) : (
                            <StarBorder
                              sx={{
                                color: 'white',
                                fontSize: 28,
                                transition: 'all 0.3s ease'
                              }}
                            />
                          )}
                        </IconButton>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Box sx={{ pl: 1 }}>
                        {isSettingsMode ? (
                          <Typography sx={{ 
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ color: '#2391FF' }}>{(localDevice || device)?.tipo || 'N/A'}</span>
                            <span style={{ color: '#2391FF' }}> - ID: {(localDevice || device)?.id || 'N/A'}</span>
                          </Typography>
                        ) : (
                          <Typography sx={{ 
                            color: (() => {
                              const currentDevice = localDevice || device;
                              const consumo = currentDevice?.tipo === 'PLUS2PM' 
                                ? ((String(currentDevice?.estado) === 'opening' || String(currentDevice?.estado) === 'closing') 
                                    ? currentDevice?.consumo || 0 
                                    : 0)
                                : (currentDevice?.consumo || 0);
                              return consumo > 0 ? '#2391FF' : 'white';
                            })(),
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            Consumo: {formatConsumption(
                              (localDevice || device)?.tipo === 'PLUS2PM' 
                                ? ((String((localDevice || device)?.estado) === 'opening' || String((localDevice || device)?.estado) === 'closing') 
                                    ? (localDevice || device)?.consumo || 0 
                                    : 0)
                                : (localDevice || device)?.consumo || 0
                            )}
                          </Typography>
                        )}
                        {isSettingsMode ? (
                          <>
                            {(localDevice || device)?.ip && (
                              <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                                IP: {(localDevice || device).ip}
                              </Typography>
                            )}
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3, display: 'flex', alignItems: 'center' }}>
                              <span>MAC:</span>
                              <span style={{ marginLeft: '4px' }}>{(localDevice || device)?.mac || 'No disponible'}</span>
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                              {t('deviceModal.applianceType')}:
                            </Typography>
                            <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.3 }}>
                              {(localDevice || device)?.tipo_artefacto || t('deviceModal.undefined')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* NO PLUS2PM: Botón a la izquierda, info a la derecha con timer */}
                    {/* Botón de power con timer - a la izquierda */}
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1 }}>
                        <PowerButton
                          isOn={isOn}
                          isUnknown={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                          isLoading={confirmingDevices.has((localDevice || device)?.id)}
                          onClick={handleToggleDevice}
                          deviceId={(localDevice || device)?.id}
                          timerConfig={(localDevice || device)?.timer_config}
                          isTimerActive={deviceTimerStates[(localDevice || device)?.id]?.isTimerActive || false}
                          timerStartTime={deviceTimerStates[(localDevice || device)?.id]?.timerStartTime}
                          timerPosition="left"
                          isDemo={userIsDemo}
                          onDemoRestriction={() => {
                            setPermissionSnackbar({
                              open: true,
                              message: t('permissions.demoRestriction')
                            });
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Estrella de favorito */}
                    {!isSettingsMode && (
                      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <IconButton
                          onClick={handleToggleFavorite}
                          sx={{
                            padding: 0.5,
                            minWidth: 'auto',
                            mr: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)'
                            }
                          }}
                        >
                          {isFavorite ? (
                            <Star
                              sx={{
                                color: '#FFD700',
                                fontSize: 28,
                                filter: 'drop-shadow(0px 0px 8px rgba(255, 215, 0, 0.8))',
                                transition: 'all 0.3s ease'
                              }}
                            />
                          ) : (
                            <StarBorder
                              sx={{
                                color: 'white',
                                fontSize: 28,
                                transition: 'all 0.3s ease'
                              }}
                            />
                          )}
                        </IconButton>
                      </Grid>
                    )}

                    {/* Información del dispositivo - solo Consumo, IP, MAC */}
                    <Grid item xs={6}>
                      <Box sx={{ pl: 1 }}>
                        {isSettingsMode ? (
                          <Typography sx={{ 
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ color: '#2391FF' }}>{(localDevice || device)?.tipo || 'N/A'}</span>
                            <span style={{ color: '#2391FF' }}> - ID: {(localDevice || device)?.id || 'N/A'}</span>
                          </Typography>
                        ) : (
                          <Typography sx={{ 
                            color: (() => {
                              const currentDevice = localDevice || device;
                              const consumo = currentDevice?.consumo || 0;
                              return consumo > 0 ? '#2391FF' : 'white';
                            })(),
                            fontSize: '0.8rem', 
                            mb: 0.3,
                            whiteSpace: 'nowrap'
                          }}>
                            {t('deviceModal.consumption')}: {formatConsumption((localDevice || device)?.consumo || 0)}
                          </Typography>
                        )}
                        {isSettingsMode ? (
                          <>
                            {(localDevice || device)?.ip && (
                              <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                                IP: {(localDevice || device).ip}
                              </Typography>
                            )}
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3, display: 'flex', alignItems: 'center' }}>
                              <span>MAC:</span>
                              <span style={{ marginLeft: '4px' }}>{(localDevice || device)?.mac || 'No disponible'}</span>
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 0.3 }}>
                              {t('deviceModal.applianceType')}:
                            </Typography>
                            <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.3 }}>
                              {(localDevice || device)?.tipo_artefacto || t('deviceModal.undefined')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                )}

              </Grid>
            </Card>
              </Box>

              {showSideSlider && (
              <Box sx={{ 
                width: '100%',
                height: '219px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                minHeight: 0,
              }}>
                {/* Slider de Brillo para DIMMER2 */}
                {(localDevice || device) && ((localDevice || device).tipo === 'DIMMER2' || (localDevice || device).tipo === 'dimmer2' || (localDevice || device).tipo?.toLowerCase() === 'dimmer2' || (localDevice || device).lights_brightness !== undefined) && (
                  <Card
                    sx={{
                      ...SLIDER_CARD_SX,
                      p: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px'
                    }}
                  >
                    <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.5 }}>
                      {t('deviceModal.brightness')}
                    </Typography>
                    <EditablePercent
                      value={currentBrightness}
                      disabled={!(localDevice || device)?.online || !!(localDevice || device)?.auth_required || !canControl}
                      ariaLabel={t('deviceModal.brightness')}
                      onCommit={(n) => {
                        void handleBrightnessChangeCommitted({} as React.SyntheticEvent, n);
                      }}
                    />
                    <Slider
                      orientation="vertical"
                      value={localBrightness}
                      onChange={handleBrightnessChange}
                      onChangeCommitted={handleBrightnessChangeCommitted}
                      disabled={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                      min={0}
                      max={100}
                      step={1}
                      sx={{
                        height: '140px',
                        width: '100%',
                        '& .MuiSlider-track': {
                          background: `linear-gradient(to top, #0d3a6b 0%, #1a5fb3 25%, #2391FF 50%, #4A9EFF 75%, #6BB6FF 100%)`,
                          border: 'none',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-thumb': {
                          backgroundColor: 'white',
                          border: 'none',
                          width: '30px', // Reducido de 42px a 30px
                          height: '6px',
                          borderRadius: '2px',
                          marginLeft: '0px',
                          marginBottom: '-7px', // Ajustado para que en 0% no se oculte
                          '&:hover': {
                            boxShadow: 'none'
                          },
                          '&:before': {
                            display: 'none'
                          }
                        }
                      }}
                    />
                  </Card>
                )}

                {/* Slider de Apertura para PLUS2PM */}
                {(localDevice || device) && (localDevice || device).tipo === 'PLUS2PM' && (
                  <Card
                    sx={{
                      ...SLIDER_CARD_SX,
                      p: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px'
                    }}
                  >
                    <Typography sx={{ ...SECTION_TITLE_SX, mb: 0.5 }}>
                      Apertura
                    </Typography>
                    <EditablePercent
                      value={localAperture}
                      disabled={!(localDevice || device)?.online || !!(localDevice || device)?.auth_required || !canControl}
                      ariaLabel="Apertura"
                      onCommit={(n) => {
                        void handleApertureChangeCommitted({} as React.SyntheticEvent, n);
                      }}
                    />
                    <Slider
                      orientation="vertical"
                      value={localAperture}
                      onChange={handleApertureChange}
                      onChangeCommitted={handleApertureChangeCommitted}
                      disabled={!(localDevice || device)?.online || (localDevice || device)?.auth_required}
                      min={0}
                      max={100}
                      step={1}
                      sx={{
                        height: '140px',
                        width: '100%',
                        '& .MuiSlider-track': {
                          background: `linear-gradient(to top, #0d3a6b 0%, #1a5fb3 25%, #2391FF 50%, #4A9EFF 75%, #6BB6FF 100%)`,
                          border: 'none',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          borderRadius: '4px',
                          width: '45%' // Ajustado para tener margenes apropiados
                        },
                        '& .MuiSlider-thumb': {
                          backgroundColor: 'white',
                          border: 'none',
                          width: '30px', // Reducido de 42px a 30px
                          height: '6px',
                          borderRadius: '2px',
                          marginLeft: '0px',
                          marginBottom: '-7px', // Ajustado para que en 0% no se oculte
                          '&:hover': {
                            boxShadow: 'none'
                          },
                          '&:before': {
                            display: 'none'
                          }
                        }
                      }}
                    />
                  </Card>
                )}
              </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Transition>

      {/* Dialog para Timer */}
      <Dialog
        open={timerDialogOpen}
        onClose={() => setTimerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 500 }}>
          {t('deviceModal.configureTimer')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={resetTimerToDefault} 
              sx={{ 
                color: '#ff6b6b', 
                fontSize: '0.8rem',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  color: '#ff5252'
                }
              }}
              size="small"
              title={t('deviceModal.resetTimer')}
            >
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
            <IconButton onClick={() => setTimerDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Auto On Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch 
                  size="small" 
                  checked={autoOnEnabled}
                  onChange={(e) => setAutoOnEnabled(e.target.checked)}
                />
                <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                  {t('deviceModal.autoOn')}
          </Typography>
              </Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.75rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.ifOffTurnOnAfter')} <span style={{ color: '#2391FF' }}>01 min</span>
              </Typography>
              <TimePickerComponent
                hours={autoOnHours}
                minutes={autoOnMinutes}
                seconds={autoOnSeconds}
                onHoursChange={handleAutoOnHoursChange}
                onMinutesChange={handleAutoOnMinutesChange}
                onSecondsChange={handleAutoOnSecondsChange}
                label={t('deviceModal.autoOn')}
              />
              <Typography sx={{ color: '#666', fontSize: '0.7rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.valueRange')}
              </Typography>
            </Box>

            {/* Auto Off Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch 
                  size="small" 
                  checked={autoOffEnabled}
                  onChange={(e) => setAutoOffEnabled(e.target.checked)}
                />
                <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                  {t('deviceModal.autoOff')}
                </Typography>
              </Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.75rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.ifOnTurnOffAfter')} <span style={{ color: '#2391FF' }}>01 min</span>
              </Typography>
              <TimePickerComponent
                hours={autoOffHours}
                minutes={autoOffMinutes}
                seconds={autoOffSeconds}
                onHoursChange={handleAutoOffHoursChange}
                onMinutesChange={handleAutoOffMinutesChange}
                onSecondsChange={handleAutoOffSecondsChange}
                label={t('deviceModal.autoOff')}
              />
              <Typography sx={{ color: '#666', fontSize: '0.7rem', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('deviceModal.valueRange')}
              </Typography>
            </Box>
          </Box>
          
          {/* Opción Una sola vez */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #444' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Switch 
                size="small" 
                checked={oneTimeOnly}
                onChange={(e) => setOneTimeOnly(e.target.checked)}
              />
              <Typography sx={{ color: 'white', ml: 1, fontSize: '0.9rem' }}>
                {t('deviceModal.oneTimeOnlyDescription')}
              </Typography>
            </Box>
          </Box>
          
          {/* Botón Guardar único */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="medium"
              onClick={async () => {
                await saveDeviceSchedules();
                setTimerDialogOpen(false);
              }}
              sx={{
                backgroundColor: '#2391FF',
                '&:hover': { backgroundColor: '#1a7ae6' },
                fontSize: '0.9rem',
                py: 1,
                px: 2.5,
                minWidth: '120px'
              }}
            >
              {t('deviceModal.saveButton')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog para Agenda */}
      <Dialog
        open={agendaDialogOpen}
        onClose={handleAgendaClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 500 }}>
          {t('deviceModal.createSchedule')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={resetAgendaToDefault} 
              sx={{ 
                color: '#ff6b6b', 
                fontSize: '0.8rem',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  color: '#ff5252'
                }
              }}
              size="small"
              title={t('deviceModal.resetAgenda')}
            >
              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
            <IconButton onClick={handleAgendaClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        {/* Navegación de pasos */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #444', px: 2 }}>
          {[t('deviceModal.stepDays'), t('deviceModal.stepTime'), t('deviceModal.stepAction'), t('deviceModal.stepSummary')].map((step, index) => (
            <Box
              key={index}
              onClick={() => setAgendaStep(index + 1)}
              sx={{
                flex: 1,
                py: 2,
                textAlign: 'center',
                borderBottom: agendaStep === index + 1 ? '2px solid #2391FF' : '2px solid transparent',
                color: agendaStep === index + 1 ? '#2391FF' : '#aaa',
                cursor: 'pointer',
                '&:hover': {
                  color: '#2391FF',
                  backgroundColor: 'rgba(35, 145, 255, 0.1)'
                }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {step}
          </Typography>
            </Box>
          ))}
        </Box>

        <DialogContent sx={{ pt: 3, minHeight: '400px' }}>
          {/* Paso 1: Días */}
          {agendaStep === 1 && (
            <Box>
              
              {/* Días de la semana - más compacto */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'space-between' }}>
                {[t('deviceModal.weekDaysShort.mon'), t('deviceModal.weekDaysShort.tue'), t('deviceModal.weekDaysShort.wed'), t('deviceModal.weekDaysShort.thu'), t('deviceModal.weekDaysShort.fri'), t('deviceModal.weekDaysShort.sat'), t('deviceModal.weekDaysShort.sun')].map((day, index) => (
                  <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'white', mb: 0.5, fontSize: '0.7rem' }}>
                      {day}
                    </Typography>
                    <Switch
                      size="small"
                      checked={selectedDays.includes(index + 1)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDays([...selectedDays, index + 1]);
                        } else {
                          setSelectedDays(selectedDays.filter(d => d !== index + 1));
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#2391FF',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#2391FF',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Seleccionar días específicos del mes */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Switch
                    size="small"
                    checked={specificDaysEnabled}
                    onChange={(e) => setSpecificDaysEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2391FF',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2391FF',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                    {t('deviceModal.selectSpecificDaysOfMonth')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.7rem' }}>
                  {t('deviceModal.appliesToAllDays')}
                </Typography>
                
                {specificDaysEnabled && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Button
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const evenDays = Array.from({ length: 31 }, (_, i) => i + 1).filter(d => d % 2 === 0);
                          setSelectedDaysOfMonth(Array.from(new Set([...selectedDaysOfMonth, ...evenDays])));
                        }}
                      >
                        {t('deviceModal.even')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const oddDays = Array.from({ length: 31 }, (_, i) => i + 1).filter(d => d % 2 === 1);
                          setSelectedDaysOfMonth(Array.from(new Set([...selectedDaysOfMonth, ...oddDays])));
                        }}
                      >
                        {t('deviceModal.odd')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => setSelectedDaysOfMonth([])}
                      >
                        {t('deviceModal.clear')}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <Button
                          key={day}
                          size="small"
                          variant={selectedDaysOfMonth.includes(day) ? 'contained' : 'outlined'}
                          sx={{
                            minWidth: '20px',
                            height: '20px',
                            fontSize: '0.6rem',
                            backgroundColor: selectedDaysOfMonth.includes(day) ? '#2391FF' : 'transparent',
                            color: selectedDaysOfMonth.includes(day) ? 'white' : '#aaa',
                            borderColor: SURFACE_BORDER,
                            '&:hover': {
                              backgroundColor: selectedDaysOfMonth.includes(day) ? '#1a7ae6' : '#333',
                            }
                          }}
                          onClick={() => {
                            if (selectedDaysOfMonth.includes(day)) {
                              setSelectedDaysOfMonth(selectedDaysOfMonth.filter(d => d !== day));
                            } else {
                              setSelectedDaysOfMonth([...selectedDaysOfMonth, day]);
                            }
                          }}
                        >
                          {day}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Seleccionar meses específicos */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Switch
                    size="small"
                    checked={specificMonthsEnabled}
                    onChange={(e) => setSpecificMonthsEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2391FF',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2391FF',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                    {t('deviceModal.selectSpecificMonths')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.7rem' }}>
                  {t('deviceModal.appliesToAllMonths')}
                </Typography>
                
                {specificMonthsEnabled && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const evenMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => m % 2 === 0);
                          setSelectedMonths(Array.from(new Set([...selectedMonths, ...evenMonths])));
                        }}
                      >
                        {t('deviceModal.even')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => {
                          const oddMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => m % 2 === 1);
                          setSelectedMonths(Array.from(new Set([...selectedMonths, ...oddMonths])));
                        }}
                      >
                        {t('deviceModal.odd')}
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#2391FF', borderColor: '#2391FF', fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                        onClick={() => setSelectedMonths([])}
                      >
                        {t('deviceModal.clear')}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                      {[t('deviceModal.months.jan'), t('deviceModal.months.feb'), t('deviceModal.months.mar'), t('deviceModal.months.apr'), t('deviceModal.months.may'), t('deviceModal.months.jun'), t('deviceModal.months.jul'), t('deviceModal.months.aug'), t('deviceModal.months.sep'), t('deviceModal.months.oct'), t('deviceModal.months.nov'), t('deviceModal.months.dec')].map((month, index) => (
                        <Button
                          key={month}
                          size="small"
                          variant={selectedMonths.includes(index + 1) ? 'contained' : 'outlined'}
                          sx={{
                            minWidth: '40px',
                            height: '25px',
                            fontSize: '0.6rem',
                            backgroundColor: selectedMonths.includes(index + 1) ? '#2391FF' : 'transparent',
                            color: selectedMonths.includes(index + 1) ? 'white' : '#aaa',
                            borderColor: SURFACE_BORDER,
                            '&:hover': {
                              backgroundColor: selectedMonths.includes(index + 1) ? '#1a7ae6' : '#333',
                            }
                          }}
                          onClick={() => {
                            if (selectedMonths.includes(index + 1)) {
                              setSelectedMonths(selectedMonths.filter(m => m !== index + 1));
                            } else {
                              setSelectedMonths([...selectedMonths, index + 1]);
                            }
                          }}
                        >
                          {month}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Paso 2: Tiempo */}
          {agendaStep === 2 && (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Typography variant="body2" sx={{ color: 'white', mb: 2, fontSize: '0.8rem' }}>
                {t('deviceModal.selectSchedules')}
              </Typography>
              
              {scheduleTimes.map((time, index) => (
                <Box key={index} sx={{ position: 'relative', mb: 0.05 }}>
                  {/* Botón X para eliminar (solo si no es el primero) */}
                  {index > 0 && (
                    <IconButton
                      onClick={() => removeScheduleTime(index)}
                      sx={{ 
                        position: 'absolute',
                        top: 4,
                        right: 8,
                        zIndex: 1,
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#ff4444',
                        '&:hover': {
                          backgroundColor: '#cc3333'
                        }
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>×</Typography>
                    </IconButton>
                  )}
                  
                  <TimePickerComponent
                    hours={time.hours}
                    minutes={time.minutes}
                    seconds={time.seconds}
                    onHoursChange={(value) => {
                      if (value > 23) {
                        setTimeError(t('deviceModal.hoursError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'hours', value);
                    }}
                    onMinutesChange={(value) => {
                      if (value > 59) {
                        setTimeError(t('deviceModal.minutesError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'minutes', value);
                    }}
                    onSecondsChange={(value) => {
                      if (value > 59) {
                        setTimeError(t('deviceModal.secondsError'));
                        return;
                      }
                      setTimeError('');
                      updateScheduleTime(index, 'seconds', value);
                    }}
                    label={`${t('deviceModal.schedule')} ${index + 1}`}
                  />
                </Box>
              ))}
              
              {/* Botón "Agregar Horario" */}
              {scheduleTimes.length < 48 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    onClick={addScheduleTime}
                    variant="outlined"
                    fullWidth
                    sx={{
                      color: '#2391FF',
                      borderColor: '#2391FF',
                      '&:hover': {
                        borderColor: '#1a7ae6',
                        backgroundColor: 'rgba(35, 145, 255, 0.1)'
                      }
                    }}
                  >
                    {t('deviceModal.addSchedule')}
                  </Button>
                </Box>
              )}
              
              {timeError && (
                <Typography variant="body2" sx={{ color: '#ff4444', mt: 1, fontSize: '0.8rem' }}>
                  {timeError}
                </Typography>
              )}
              
              <Typography variant="body2" sx={{ color: '#aaa', mt: 2, fontSize: '0.7rem' }}>
                {scheduleTimes.length} {t('deviceModal.schedulesConfigured')}
              </Typography>
            </Box>
          )}

          {/* Paso 3: Acción */}
          {agendaStep === 3 && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 1, fontSize: '0.8rem' }}>
                  {t('deviceModal.selectAction')}
                </Typography>
                <RadioGroup
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}
                >
                  {['On', 'Off', 'Cambiar Estado'].map((action) => (
                    <FormControlLabel
                      key={action}
                      value={action}
                      control={
                        <Radio
                          sx={{
                            color: '#2391FF',
                            '&.Mui-checked': {
                              color: '#2391FF',
                            },
                          }}
                        />
                      }
                      label={action === 'On' ? t('deviceModal.turnOn') : action === 'Off' ? t('deviceModal.turnOff') : t('deviceModal.changeState')}
                      sx={{ color: 'white', fontSize: '0.8rem' }}
                    />
                  ))}
                </RadioGroup>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={toggleEnabled}
                      onChange={(e) => setToggleEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#2391FF',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#2391FF',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                      {t('deviceModal.toggleAfterTime')}
                    </Typography>
                  }
                />
                <Typography variant="body2" sx={{ color: '#aaa', mt: 1, fontSize: '0.7rem' }}>
                  {t('deviceModal.returnsToPreviousState')}
                </Typography>
                
                {toggleEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <TimePickerComponent
                      hours={Math.floor(toggleAfterSeconds / 3600)}
                      minutes={Math.floor((toggleAfterSeconds % 3600) / 60)}
                      seconds={toggleAfterSeconds % 60}
                      onHoursChange={(value) => {
                        const minutes = Math.floor((toggleAfterSeconds % 3600) / 60);
                        const seconds = toggleAfterSeconds % 60;
                        const maxHours = 11424; // 476 días en horas
                        const newTotalSeconds = value * 3600 + minutes * 60 + seconds;
                        
                        if (newTotalSeconds > maxHours * 3600) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      onMinutesChange={(value) => {
                        // Transformación automática: si minutos >= 60, convertir a horas
                        let hours = Math.floor(toggleAfterSeconds / 3600);
                        let minutes = value;
                        let seconds = toggleAfterSeconds % 60;
                        
                        // Aplicar transformación automática inmediatamente
                        if (minutes >= 60) {
                          hours += Math.floor(minutes / 60);
                          minutes = minutes % 60;
                        }
                        
                        const newTotalSeconds = hours * 3600 + minutes * 60 + seconds;
                        const maxSeconds = 11424 * 3600; // 476 días en segundos
                        
                        if (newTotalSeconds > maxSeconds) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      onSecondsChange={(value) => {
                        // Transformación automática: si segundos >= 60, convertir a minutos
                        let hours = Math.floor(toggleAfterSeconds / 3600);
                        let minutes = Math.floor((toggleAfterSeconds % 3600) / 60);
                        let seconds = value;
                        
                        // Aplicar transformación automática inmediatamente
                        if (seconds >= 60) {
                          minutes += Math.floor(seconds / 60);
                          seconds = seconds % 60;
                          
                          // Si minutos >= 60, convertir a horas
                          if (minutes >= 60) {
                            hours += Math.floor(minutes / 60);
                            minutes = minutes % 60;
                          }
                        }
                        
                        const newTotalSeconds = hours * 3600 + minutes * 60 + seconds;
                        const maxSeconds = 11424 * 3600; // 476 días en segundos
                        
                        if (newTotalSeconds > maxSeconds) {
                          setDurationError(t('deviceModal.timeCannotExceed'));
                          return;
                        }
                        setDurationError('');
                        setToggleAfterSeconds(newTotalSeconds);
                      }}
                      label={t('deviceModal.alternationTime')}
                    />
                    {durationError && (
                      <Typography variant="body2" sx={{ color: '#ff4444', mt: 1, fontSize: '0.8rem' }}>
                        {durationError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Paso 4: Resumen */}
          {agendaStep === 4 && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.days')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    {t('deviceModal.executeSchedule')} <span style={{ color: '#2391FF' }}>
                      {selectedDays.length === 7 ? t('deviceModal.allDays') : 
                       selectedDays.length === 0 ? t('deviceModal.noDays') :
                       selectedDays.map(d => [t('deviceModal.weekDays.mon'), t('deviceModal.weekDays.tue'), t('deviceModal.weekDays.wed'), t('deviceModal.weekDays.thu'), t('deviceModal.weekDays.fri'), t('deviceModal.weekDays.sat'), t('deviceModal.weekDays.sun')][d-1]).join(', ')}
                    </span>
                    {specificDaysEnabled && selectedDaysOfMonth.length > 0 && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.days')} {selectedDaysOfMonth.join(', ')})</span>
                    )}
                    {specificMonthsEnabled && selectedMonths.length > 0 && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.month')} {selectedMonths.map(m => [t('deviceModal.months.jan'), t('deviceModal.months.feb'), t('deviceModal.months.mar'), t('deviceModal.months.apr'), t('deviceModal.months.may'), t('deviceModal.months.jun'), t('deviceModal.months.jul'), t('deviceModal.months.aug'), t('deviceModal.months.sep'), t('deviceModal.months.oct'), t('deviceModal.months.nov'), t('deviceModal.months.dec')][m-1]).join(', ')})</span>
                    )}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.schedules')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    {t('deviceModal.executeScheduleAt')} <span style={{ color: '#2391FF' }}>
                      {scheduleTimes.map(time => 
                        `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:${time.seconds.toString().padStart(2, '0')}`
                      ).join(', ')}
                    </span>
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 0.5, fontSize: '0.8rem' }}>
                    {t('deviceModal.action')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontSize: '0.9rem' }}>
                    <span style={{ color: '#2391FF' }}>
                      {actionType === 'On' ? t('deviceModal.turnOn') : actionType === 'Off' ? t('deviceModal.turnOff') : actionType === 'Cambiar Estado' ? t('deviceModal.changeState') : t('deviceModal.toggle')}
                    </span>
                    {toggleEnabled && (
                      <span style={{ color: '#2391FF' }}> ({t('deviceModal.changesAfter')} {Math.floor(toggleAfterSeconds / 3600)}h {Math.floor((toggleAfterSeconds % 3600) / 60)}m {toggleAfterSeconds % 60}s)</span>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #444', p: 2 }}>
          {agendaStep > 1 && (
          <Button
              onClick={handleAgendaBack}
              sx={{
                color: 'white',
                borderColor: SURFACE_BORDER,
                '&:hover': {
                  borderColor: '#2391FF',
                  backgroundColor: '#333'
                }
              }}
              variant="outlined"
            >
              {t('deviceModal.back')}
          </Button>
          )}
          
          <Box sx={{ flex: 1 }} />
          
          {agendaStep < 4 ? (
            <Button
              onClick={handleAgendaNext}
              sx={{
                backgroundColor: '#2391FF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1a7ae6'
                }
              }}
            >
              {t('deviceModal.next')}
            </Button>
          ) : (
            <Button
              onClick={handleAgendaSave}
              sx={{
                backgroundColor: '#2391FF',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1a7ae6'
                }
              }}
            >
              {t('deviceModal.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de fecha personalizada (igual que HistoricalReports) */}
      <Dialog
        open={customDateDialogOpen}
        onClose={() => setCustomDateDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: 'rgba(18,18,20,0.98)', color: 'white', borderRadius: '16px', minWidth: '300px', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: '1rem', fontWeight: 700, borderBottom: `1px solid ${SURFACE_BORDER}`, paddingBottom: 1 }}>
          {t('deviceModal.customPeriod')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            label={t('deviceModal.from')}
            type="datetime-local"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputLabel-root': { color: '#999' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER } }}
          />
          <TextField
            label={t('deviceModal.to')}
            type="datetime-local"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputLabel-root': { color: '#999' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: SURFACE_BORDER } }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px', borderTop: '1px solid #444' }}>
          <Button onClick={() => setCustomDateDialogOpen(false)} sx={{ color: '#999', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>{t('deviceModal.cancel')}</Button>
          <Button onClick={handleCustomDateConfirm} sx={{ color: '#2391FF', fontWeight: 'bold', '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' } }}>{t('deviceModal.confirm')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes de permisos */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert
          onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default DeviceModal;
